import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Avatar, Button, Col, DatePicker, Empty, Input, InputNumber, Modal, Row, Slider, Space, Spin, Switch, Tabs, Tag, Typography } from 'antd';
import { CheckOutlined, FullscreenOutlined, LeftOutlined, RightOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import Hls from 'hls.js';
import { Link, useSearchParams } from 'react-router-dom';
import { MediaOutlet, MediaPlayer, useMediaStore } from '@vidstack/react';
import { isHLSProvider, type MediaPlayerElement } from 'vidstack';
import { useMediaRemote } from '@vidstack/react';
import { fetchAuthSession } from '../auth/gateway';
import { useDevice } from '../contexts/DeviceContext';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { buildDayRange, parseMeasuredAt } from '../components/location-analysis/dateUtils';
import { JST_TIMEZONE } from '../components/location-analysis/analysisConfig';
import LocationAnalysisMap from '../components/location-analysis/LocationAnalysisMap';
import LocationAnalysisResultView, { LocationAnalysisTimelinePanel } from '../components/location-analysis/LocationAnalysisResultView';
import useLocationAnalysisDerivedData from '../components/location-analysis/useLocationAnalysisDerivedData';
import type { DerivedTimelineTarget } from '../components/location-analysis/timelineTypes';
import {
  normalizePlaybackTimeline,
  resolvePlaybackSecondAtTimeMs,
  resolvePositionAtPlaybackSecond,
  resolveTimeMsAtPlaybackSecond,
  type PlaybackTimeline,
} from '../components/timelapse/playbackTimeline';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import { ensureFaceMediaCookie } from '../utils/faceMediaCookie';
import type { DailyEncounterPerson, DailyEncounterResponse } from '../types/api';

const { Text } = Typography;

type LatestPhoto = { captured_at?: string; received_at?: string; timestamp?: string };
type LatestAudio = { start_time?: string; received_at?: string };
type WebSocketMessage = {
  type: string;
  device_id?: string;
  data?: {
    day?: string;
    timezone?: string;
    start?: string;
    end?: string;
  };
};
type TimelapseJob = {
  status: string;
  job_id?: string | null;
  video_url?: string | null;
  metadata_url?: string | null;
  error_message?: string | null;
  submitted_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
};
type TimelapseMediaCookieResponse = {
  enabled?: boolean;
  domain?: string | null;
  expires_at?: number | null;
};
type TimelapseMeta = {
  frameCount?: number;
  fps?: number;
  width?: number;
  height?: number;
  codec?: string;
  bitrate?: string;
  durationSec?: number;
  capturedStart?: string;
  capturedEnd?: string;
  frameTimes?: number[];
  playbackTimeline?: PlaybackTimeline | null;
  streaming?: {
    mode?: string;
    playbackUri?: string;
    masterPlaylistUri?: string;
    renditions?: Array<Record<string, unknown>>;
  };
};
type TimelapseQualityOption = {
  mediaIndex: number;
  levelIndex: number;
  label: string;
  width: number;
  height: number;
  bitrate?: number;
};
type TimelapseChapter = {
  eventIndex: number;
  title: string;
  startLabel: string;
  endLabel: string;
  startSec: number;
  endSec: number;
};
type EventMapFocusTarget = {
  type: 'location' | 'staypoint' | 'tripleg';
  id: string;
};

const resolveTimelapseMediaType = (url: string): string => {
  const pathname = (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  })().toLowerCase();
  return pathname.endsWith('.mp4') ? 'video/mp4' : 'application/x-mpegurl';
};

type DiarySummaryEvent = {
  start: string;
  end: string;
  title: string;
  detail: string;
  hasData?: number | boolean;
};
type DiarySummaryPayload = {
  events: DiarySummaryEvent[];
};
type GeminiUsageMetadata = {
  promptTokenCount?: number;
  totalTokenCount?: number;
  thoughtsTokenCount?: number;
  promptTokensDetails?: Array<{ modality?: string; tokenCount?: number }>;
};
type DiarySummaryInputStats = {
  gemini_usage?: GeminiUsageMetadata;
  generation_config?: {
    model?: string;
    max_output_tokens?: number;
    include_thoughts?: boolean;
    thinking_budget?: number | null;
    enable_compaction?: boolean;
    temperature?: number;
  };
};
type DiarySummaryRecord = {
  status: string;
  summary?: DiarySummaryPayload | null;
  input_stats?: DiarySummaryInputStats | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
};
type ConversationSummaryRecord = {
  status: string;
  summary?: Record<string, unknown> | null;
  input_stats?: Record<string, unknown> | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
};
type FoodSummaryItem = {
  start?: string;
  place?: string;
  contents?: string;
  calory?: string;
};
type FoodSummaryPayload = {
  drinksandeats?: FoodSummaryItem[];
};
type FoodSummaryRecord = {
  status: string;
  summary?: FoodSummaryPayload | Record<string, unknown> | null;
  input_stats?: Record<string, unknown> | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
};

type DiaryPageProps = {
  onWebSocketMessage?: (handler: ((message: WebSocketMessage) => void) | null) => void;
};

type DiarySummaryRequestOptions = {
  maxOutputTokens: number;
  includeThoughts: boolean;
  thinkingBudget: number | null;
  enableCompaction: boolean;
  userPrompt: string;
};

const DEFAULT_DIARY_SUMMARY_REQUEST_OPTIONS: DiarySummaryRequestOptions = {
  maxOutputTokens: 4096,
  includeThoughts: false,
  thinkingBudget: 512,
  enableCompaction: true,
  userPrompt: '',
};

const VIDSTACK_HLS_CDN_URL = 'https://cdn.jsdelivr.net/npm/hls.js@^1.0.0/dist/hls.min.js';
const VIDSTACK_HLS_CDN_ORIGIN = 'https://cdn.jsdelivr.net';
let bundledVidstackHlsGuardInstalled = false;

const isBlockedVidstackHlsNode = (node: Node | string) => {
  if (typeof node === 'string') {
    return false;
  }
  if (node instanceof HTMLLinkElement) {
    return node.href.startsWith(VIDSTACK_HLS_CDN_ORIGIN);
  }
  if (node instanceof HTMLScriptElement) {
    return node.src.startsWith(VIDSTACK_HLS_CDN_ORIGIN) || node.src.startsWith(VIDSTACK_HLS_CDN_URL);
  }
  return false;
};

const resolveBundledVidstackHlsNode = (node: Node | string) => {
  if (!(node instanceof HTMLScriptElement)) {
    return;
  }
  queueMicrotask(() => {
    node.dispatchEvent(new Event('load'));
    node.onload?.(new Event('load'));
  });
};

const ensureBundledVidstackHls = () => {
  if (bundledVidstackHlsGuardInstalled || typeof window === 'undefined') {
    return;
  }
  bundledVidstackHlsGuardInstalled = true;
  (window as Window & { Hls?: typeof Hls }).Hls = Hls;

  const originalAppend = Element.prototype.append;
  Element.prototype.append = function patchedAppend(...nodes: (Node | string)[]) {
    const allowedNodes = nodes.filter((node) => {
      if (!isBlockedVidstackHlsNode(node)) {
        return true;
      }
      resolveBundledVidstackHlsNode(node);
      return false;
    });
    if (!allowedNodes.length) {
      return;
    }
    return originalAppend.apply(this, allowedNodes);
  };

  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function patchedAppendChild<T extends Node>(node: T) {
    if (isBlockedVidstackHlsNode(node)) {
      resolveBundledVidstackHlsNode(node);
      return node;
    }
    return originalAppendChild.call(this, node);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchedInsertBefore<T extends Node>(node: T, child: Node | null) {
    if (isBlockedVidstackHlsNode(node)) {
      resolveBundledVidstackHlsNode(node);
      return node;
    }
    return originalInsertBefore.call(this, node, child);
  };
};

ensureBundledVidstackHls();

const isValidDateString = (dateStr: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return (
    Number.isFinite(year) &&
    Number.isFinite(month) &&
    Number.isFinite(day) &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const extractDateString = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  const prefix = trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(prefix)) {
    return prefix;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const buildLocalRange = (dateStr: string) => {
  if (!isValidDateString(dateStr)) {
    return null;
  }
  const [year, month, day] = dateStr.split('-').map(Number);
  const start = new Date(year, month - 1, day).toISOString();
  const end = new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
  return { start, end };
};

const buildDiarySummaryOptions = (
  stats?: DiarySummaryInputStats | null,
): DiarySummaryRequestOptions => {
  const generationConfig = stats?.generation_config;
  return {
    maxOutputTokens: generationConfig?.max_output_tokens ?? DEFAULT_DIARY_SUMMARY_REQUEST_OPTIONS.maxOutputTokens,
    includeThoughts: generationConfig?.include_thoughts ?? DEFAULT_DIARY_SUMMARY_REQUEST_OPTIONS.includeThoughts,
    thinkingBudget:
      generationConfig?.thinking_budget ?? DEFAULT_DIARY_SUMMARY_REQUEST_OPTIONS.thinkingBudget,
    enableCompaction:
      generationConfig?.enable_compaction ?? DEFAULT_DIARY_SUMMARY_REQUEST_OPTIONS.enableCompaction,
    userPrompt: '',
  };
};

const DiaryPage: React.FC<DiaryPageProps> = ({ onWebSocketMessage }) => {
  const { selectedDevice } = useDevice();
  const { setPageHeader } = usePageHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousDeviceIdRef = useRef<string | null>(null);
  const skipUrlSyncRef = useRef(false);
  const queryDateRef = useRef<string | null>(null);
  const datePickerOpenRef = useRef(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerPanelValue, setDatePickerPanelValue] = useState<Dayjs | null>(null);
  const [initializing, setInitializing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioCount, setAudioCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [timelapseJob, setTimelapseJob] = useState<TimelapseJob | null>(null);
  const [timelapseLoading, setTimelapseLoading] = useState(false);
  const [timelapseSubmitting, setTimelapseSubmitting] = useState(false);
  const [timelapseMeta, setTimelapseMeta] = useState<TimelapseMeta | null>(null);
  const [diarySummary, setDiarySummary] = useState<DiarySummaryRecord | null>(null);
  const [diarySummaryLoading, setDiarySummaryLoading] = useState(false);
  const [diarySummarySubmitting, setDiarySummarySubmitting] = useState(false);
  const [diarySummaryModalOpen, setDiarySummaryModalOpen] = useState(false);
  const [diarySummaryRequestOptions, setDiarySummaryRequestOptions] = useState<DiarySummaryRequestOptions>(
    DEFAULT_DIARY_SUMMARY_REQUEST_OPTIONS,
  );
  const [conversationSummary, setConversationSummary] = useState<ConversationSummaryRecord | null>(null);
  const [conversationSummaryLoading, setConversationSummaryLoading] = useState(false);
  const [conversationSummarySubmitting, setConversationSummarySubmitting] = useState(false);
  const [foodSummary, setFoodSummary] = useState<FoodSummaryRecord | null>(null);
  const [foodSummaryLoading, setFoodSummaryLoading] = useState(false);
  const [foodSummarySubmitting, setFoodSummarySubmitting] = useState(false);
  const [dailyEncounterPeople, setDailyEncounterPeople] = useState<DailyEncounterPerson[]>([]);
  const [dailyEncounterPeopleLoading, setDailyEncounterPeopleLoading] = useState(false);
  const [selectedTranscriptEventIndex, setSelectedTranscriptEventIndex] = useState<number | null>(null);
  const summaryViewportRef = useRef<HTMLDivElement | null>(null);
  const summaryLeftRef = useRef<HTMLDivElement | null>(null);
  const summaryActivityRef = useRef<HTMLDivElement | null>(null);
  const summaryMiddleRef = useRef<HTMLDivElement | null>(null);
  const timelapseContainerRef = useRef<HTMLDivElement | null>(null);
  const timelapseSettingsButtonRef = useRef<HTMLButtonElement | null>(null);
  const timelapseSettingsPanelRef = useRef<HTMLDivElement | null>(null);
  const timelapseChapterToastFadeTimerRef = useRef<number | null>(null);
  const timelapseChapterToastHideTimerRef = useRef<number | null>(null);
  const [summaryMaxHeight, setSummaryMaxHeight] = useState<number | null>(null);
  const [timelapseLocationMapElement, setTimelapseLocationMapElement] = useState<HTMLDivElement | null>(null);
  const [timelapseLocationMapHeight, setTimelapseLocationMapHeight] = useState(240);
  const videoRef = useRef<MediaPlayerElement | null>(null);
  const mediaState = useMediaStore(videoRef);
  const mediaRemote = useMediaRemote(videoRef);
  const [timelapseLoopEnabled, setTimelapseLoopEnabled] = useState(true);
  const [timelapseSeekValue, setTimelapseSeekValue] = useState<number | null>(null);
  const [timelapseHovered, setTimelapseHovered] = useState(false);
  const [timelapseSettingsOpen, setTimelapseSettingsOpen] = useState(false);
  const [timelapseSettingsView, setTimelapseSettingsView] = useState<'root' | 'quality' | 'speed'>('root');
  const [timelapseFullscreenHost, setTimelapseFullscreenHost] = useState<HTMLElement | null>(null);
  const [timelapseSettingsPanelOffset, setTimelapseSettingsPanelOffset] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);
  const [hoveredTimelapseChapterIndex, setHoveredTimelapseChapterIndex] = useState<number | null>(null);
  const [timelapseChapterToast, setTimelapseChapterToast] = useState<{
    title: string;
    visible: boolean;
  } | null>(null);
  const [timelapseSelectedQualityLabel, setTimelapseSelectedQualityLabel] = useState('Auto');
  const [timelapseQualitySelectionMode, setTimelapseQualitySelectionMode] = useState<'auto' | 'manual'>('auto');
  const [timelapseSelectedPlaybackRate, setTimelapseSelectedPlaybackRate] = useState(1);
  const [timelapseCookieReady, setTimelapseCookieReady] = useState(false);
  const [timelapseCookieLoading, setTimelapseCookieLoading] = useState(false);
  const [timelapseCookieError, setTimelapseCookieError] = useState<string | null>(null);
  const [timelapseCookieExpiresAt, setTimelapseCookieExpiresAt] = useState<number | null>(null);
  const [timelapsePlaybackState, setTimelapsePlaybackState] = useState(() => ({
    currentTime: 0,
    duration: 0,
    paused: true,
    canPlay: false,
    error: false,
    playbackRate: 1,
  }));
  const pendingSeekRef = useRef<number | null>(null);
  const timelapseCookieRequestInFlightRef = useRef<Promise<void> | null>(null);
  const getTimelapseHlsInstance = useCallback((): Hls | null => {
    const playerEl = videoRef.current as (MediaPlayerElement & {
      provider?: {
        instance?: Hls;
      };
    }) | null;
    return playerEl?.provider?.instance ?? null;
  }, []);
  const getTimelapseQualityCollection = useCallback(() => {
    const playerEl = videoRef.current as (MediaPlayerElement & {
      qualities?: {
        length: number;
        selectedIndex: number;
        auto?: boolean;
        autoSelect?: () => void;
        [index: number]: {
          selected?: boolean;
          width?: number;
          height?: number;
        };
      };
    }) | null;
    return playerEl?.qualities ?? null;
  }, []);
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );
  const issueTimelapseMediaCookie = useCallback(async (): Promise<void> => {
    if (!selectedDevice || !selectedDate || !timelapseJob?.job_id || timelapseJob.status !== 'succeeded') {
      setTimelapseCookieReady(false);
      setTimelapseCookieExpiresAt(null);
      return;
    }
    if (timelapseCookieRequestInFlightRef.current) {
      return timelapseCookieRequestInFlightRef.current;
    }

    const request = (async () => {
      setTimelapseCookieLoading(true);
      setTimelapseCookieError(null);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('No access token for timelapse cookie request');
        }
        const params = new URLSearchParams({
          day: selectedDate,
          timezone: timeZone,
        });
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/timelapse/media-cookie?${params.toString()}`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          },
        );
        if (!response.ok) {
          throw new Error(`Timelapse media cookie request failed: ${response.status}`);
        }
        const payload = (await response.json()) as TimelapseMediaCookieResponse;
        if (payload.enabled === false) {
          throw new Error('Timelapse media cookie is disabled');
        }
        setTimelapseCookieReady(true);
        setTimelapseCookieExpiresAt(
          typeof payload.expires_at === 'number' && Number.isFinite(payload.expires_at) ? payload.expires_at : null,
        );
      } catch (error) {
        secureLogger.error('Failed to issue timelapse media cookie', {
          error: 'Timelapse media cookie request failed',
        });
        setTimelapseCookieReady(false);
        setTimelapseCookieExpiresAt(null);
        setTimelapseCookieError('timelapse の視聴権限取得に失敗');
      } finally {
        setTimelapseCookieLoading(false);
        timelapseCookieRequestInFlightRef.current = null;
      }
    })();

    timelapseCookieRequestInFlightRef.current = request;
    return request;
  }, [selectedDate, selectedDevice, timeZone, timelapseJob?.job_id, timelapseJob?.status]);
  const queryDate = useMemo(() => {
    const raw = searchParams.get('date') ?? searchParams.get('day');
    return raw && isValidDateString(raw) ? raw : null;
  }, [searchParams]);
  const queryEventIndex = useMemo(() => {
    const raw = searchParams.get('event');
    if (raw == null || raw.trim() === '') {
      return null;
    }
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  }, [searchParams]);

  useEffect(() => {
    queryDateRef.current = queryDate;
  }, [queryDate]);

  const dateRange = useMemo(() => {
    return selectedDate ? buildLocalRange(selectedDate) : null;
  }, [selectedDate]);

  const locationAnalysisRange = useMemo(() => {
    if (!selectedDate || !isValidDateString(selectedDate)) {
      return null;
    }
    return buildDayRange(dayjs(selectedDate));
  }, [selectedDate]);

  const locationAnalysisDerived = useLocationAnalysisDerivedData({
    deviceId: selectedDevice?.id,
    rangeStartIso: locationAnalysisRange?.startIso,
    rangeEndIso: locationAnalysisRange?.endIso,
  });

  const fetchLatestDate = useCallback(async () => {
    if (!selectedDevice) {
      return;
    }
    const applyLatestDate = (nextDate: string | null) => {
      if (queryDateRef.current) {
        return;
      }
      setSelectedDate(nextDate);
    };
    setInitializing(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for diary initialization');
        return;
      }

      const [photoResp, audioResp] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const photoDate = await (async () => {
        if (!photoResp.ok) return null;
        const payload = await photoResp.json();
        const latest: LatestPhoto | undefined = payload.photos?.[0];
        const ts = latest?.captured_at || latest?.received_at || latest?.timestamp;
        return extractDateString(ts);
      })();

      const audioDate = await (async () => {
        if (!audioResp.ok) return null;
        const payload = await audioResp.json();
        const latest: LatestAudio | undefined = payload.audio_files?.[0];
        const ts = latest?.start_time || latest?.received_at;
        return extractDateString(ts);
      })();

      if (photoDate && audioDate) {
        const older = dayjs(photoDate).isBefore(dayjs(audioDate)) ? photoDate : audioDate;
        applyLatestDate(older);
        return;
      }

      if (photoDate || audioDate) {
        applyLatestDate(photoDate ?? audioDate ?? null);
        return;
      }

      applyLatestDate(dayjs().format('YYYY-MM-DD'));
    } catch (error) {
      secureLogger.error('Failed to initialize diary date', { error: 'Diary init failed' });
      applyLatestDate(dayjs().format('YYYY-MM-DD'));
    } finally {
      setInitializing(false);
    }
  }, [selectedDevice]);

  const fetchDailyCounts = useCallback(async () => {
    if (!selectedDevice || !dateRange) {
      return;
    }
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for diary counts request');
        return;
      }

      const { start, end } = dateRange;
      const [photoResp, audioResp] = await Promise.all([
        fetch(
          `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos?from_time=${start}&to_time=${end}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        fetch(
          `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio?from_time=${start}&to_time=${end}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);

      if (photoResp.ok) {
        const data = await photoResp.json();
        setPhotoCount(data.total ?? (data.photos?.length ?? 0));
      } else {
        setPhotoCount(0);
      }

      if (audioResp.ok) {
        const data = await audioResp.json();
        setAudioCount(data.total ?? (data.audio_files?.length ?? 0));
      } else {
        setAudioCount(0);
      }
    } catch (error) {
      secureLogger.error('Failed to fetch diary counts', { error: 'Diary count fetch failed' });
    } finally {
      setLoading(false);
    }
  }, [selectedDevice, dateRange]);

  const fetchTimelapseStatus = useCallback(async () => {
    if (!selectedDevice || !selectedDate) {
      return;
    }
    setTimelapseLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for timelapse status request');
        return;
      }

      const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/timelapse?day=${encodeURIComponent(
        selectedDate,
      )}&timezone=${encodeURIComponent(timeZone)}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setTimelapseJob((prev) => {
          if (
            prev &&
            prev.job_id &&
            data?.job_id &&
            prev.job_id === data.job_id &&
            prev.status === data.status &&
            prev.video_url
          ) {
            return { ...data, video_url: prev.video_url };
          }
          return data;
        });
      } else if (response.status === 404) {
        setTimelapseJob(null);
      } else {
        secureLogger.error('Timelapse status API failed', { status: response.status });
      }
    } catch (error) {
      secureLogger.error('Failed to fetch timelapse status', { error: 'Timelapse fetch failed' });
    } finally {
      setTimelapseLoading(false);
    }
  }, [selectedDevice, selectedDate, timeZone]);

  const requestTimelapse = useCallback(async () => {
    if (!selectedDevice || !selectedDate) {
      return;
    }
    setTimelapseSubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for timelapse request');
        return;
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/timelapse`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ day: selectedDate, timezone: timeZone }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        setTimelapseJob((prev) => {
          if (
            prev &&
            prev.job_id &&
            data?.job_id &&
            prev.job_id === data.job_id &&
            prev.status === data.status &&
            prev.video_url
          ) {
            return { ...data, video_url: prev.video_url };
          }
          return data;
        });
      } else if (response.status === 409) {
        await fetchTimelapseStatus();
      } else {
        secureLogger.error('Timelapse request failed', { status: response.status });
      }
    } catch (error) {
      secureLogger.error('Failed to request timelapse', { error: 'Timelapse request failed' });
    } finally {
      setTimelapseSubmitting(false);
    }
  }, [selectedDevice, selectedDate, timeZone, fetchTimelapseStatus]);

  const fetchDiarySummary = useCallback(async () => {
    if (!selectedDevice || !selectedDate) {
      return;
    }
    setDiarySummaryLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for diary summary request');
        return;
      }
      const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-summary?day=${encodeURIComponent(
        selectedDate,
      )}&timezone=${encodeURIComponent(timeZone)}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDiarySummary(data);
      } else if (response.status === 404) {
        setDiarySummary(null);
      } else {
        secureLogger.error('Diary summary API failed', { status: response.status });
      }
    } catch (error) {
      secureLogger.error('Failed to fetch diary summary', { error: 'Diary summary fetch failed' });
    } finally {
      setDiarySummaryLoading(false);
    }
  }, [selectedDevice, selectedDate, timeZone]);

  const requestDiarySummary = useCallback(async (options: DiarySummaryRequestOptions) => {
    if (!selectedDevice || !selectedDate) {
      return;
    }
    setDiarySummarySubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for diary summary request');
        return;
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-summary`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            day: selectedDate,
            timezone: timeZone,
            max_output_tokens: options.maxOutputTokens,
            include_thoughts: options.includeThoughts,
            thinking_budget: options.thinkingBudget,
            enable_compaction: options.enableCompaction,
            user_prompt: options.userPrompt.trim() || null,
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setDiarySummary(data);
        setDiarySummaryModalOpen(false);
      } else if (response.status === 409) {
        await fetchDiarySummary();
      } else {
        const errorBody = await response.json().catch(() => ({}));
        secureLogger.error('Diary summary request failed', { status: response.status, error: errorBody });
      }
    } catch (error) {
      secureLogger.error('Failed to request diary summary', { error: 'Diary summary request failed' });
    } finally {
      setDiarySummarySubmitting(false);
    }
  }, [selectedDevice, selectedDate, timeZone, fetchDiarySummary]);

  const openDiarySummaryModal = useCallback(() => {
    setDiarySummaryRequestOptions(buildDiarySummaryOptions(diarySummary?.input_stats));
    setDiarySummaryModalOpen(true);
  }, [diarySummary?.input_stats]);

  const fetchFoodSummary = useCallback(async () => {
    if (!selectedDevice || !selectedDate) {
      return;
    }
    setFoodSummaryLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for food summary fetch');
        return;
      }
      const params = new URLSearchParams({ day: selectedDate, timezone: timeZone });
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-food-summary?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 404) {
        setFoodSummary(null);
        return;
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        secureLogger.error('Food summary fetch failed', { status: response.status, error: errorBody });
        return;
      }
      const data = await response.json();
      setFoodSummary(data);
    } catch (error) {
      secureLogger.error('Food summary fetch failed', { error: 'Food summary fetch failed' });
    } finally {
      setFoodSummaryLoading(false);
    }
  }, [selectedDevice, selectedDate, timeZone]);

  const requestFoodSummary = useCallback(async () => {
    if (!selectedDevice || !selectedDate) {
      return;
    }
    setFoodSummarySubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for food summary request');
        return;
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-food-summary`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ day: selectedDate, timezone: timeZone }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setFoodSummary(data);
      } else if (response.status === 409) {
        await fetchFoodSummary();
      } else {
        const errorBody = await response.json().catch(() => ({}));
        secureLogger.error('Food summary request failed', { status: response.status, error: errorBody });
      }
    } catch (error) {
      secureLogger.error('Food summary request failed', { error: 'Food summary request failed' });
    } finally {
      setFoodSummarySubmitting(false);
    }
  }, [selectedDevice, selectedDate, timeZone, fetchFoodSummary]);

  const fetchDailyEncounterPeople = useCallback(async () => {
    if (!selectedDevice || !selectedDate) {
      setDailyEncounterPeople([]);
      return;
    }
    setDailyEncounterPeopleLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for daily encounter people fetch');
        return;
      }
      await ensureFaceMediaCookie(token);
      const params = new URLSearchParams({
        device_id: selectedDevice.id,
        day: selectedDate,
        timezone: timeZone,
      });
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/persons/daily-encounters?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        secureLogger.error('Daily encounter people fetch failed', { status: response.status, error: errorBody });
        setDailyEncounterPeople([]);
        return;
      }
      const data = (await response.json()) as DailyEncounterResponse;
      setDailyEncounterPeople(Array.isArray(data.people) ? data.people : []);
    } catch (error) {
      secureLogger.error('Daily encounter people fetch failed', { error: 'Daily encounter people fetch failed' });
      setDailyEncounterPeople([]);
    } finally {
      setDailyEncounterPeopleLoading(false);
    }
  }, [selectedDate, selectedDevice, timeZone]);

  const diaryEvents = useMemo(() => diarySummary?.summary?.events ?? [], [diarySummary?.summary?.events]);
  const selectedTranscriptEvent =
    selectedTranscriptEventIndex != null ? diaryEvents[selectedTranscriptEventIndex] : null;
  const isEventDetailMode = queryEventIndex != null && selectedTranscriptEventIndex === queryEventIndex;

  const fetchConversationSummary = useCallback(
    async (event: DiarySummaryEvent) => {
      if (!selectedDevice || !selectedDate) {
        return;
      }
      setConversationSummaryLoading(true);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          secureLogger.error('No token for conversation summary fetch');
          return;
        }
        const params = new URLSearchParams({
          day: selectedDate,
          start: event.start,
          end: event.end,
          timezone: timeZone,
        });
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-conversation-summary?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.status === 404) {
          setConversationSummary(null);
          return;
        }
        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          secureLogger.error('Conversation summary fetch failed', { status: response.status, error: errorBody });
          return;
        }
        const data = await response.json();
        setConversationSummary(data);
      } catch (error) {
        secureLogger.error('Conversation summary fetch failed', { error: 'Conversation summary fetch failed' });
      } finally {
        setConversationSummaryLoading(false);
      }
    },
    [selectedDevice, selectedDate, timeZone],
  );

  const requestConversationSummary = useCallback(async () => {
    if (
      !selectedDevice ||
      !selectedDate ||
      !selectedTranscriptEvent ||
      selectedTranscriptEvent.hasData === 0 ||
      selectedTranscriptEvent.hasData === false
    ) {
      return;
    }
    setConversationSummarySubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for conversation summary request');
        return;
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/diary-conversation-summary`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            day: selectedDate,
            start: selectedTranscriptEvent.start,
            end: selectedTranscriptEvent.end,
            timezone: timeZone,
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        setConversationSummary(data);
      } else if (response.status === 409) {
        await fetchConversationSummary(selectedTranscriptEvent);
      } else {
        const errorBody = await response.json().catch(() => ({}));
        secureLogger.error('Conversation summary request failed', { status: response.status, error: errorBody });
      }
    } catch (error) {
      secureLogger.error('Conversation summary request failed', { error: 'Conversation summary request failed' });
    } finally {
      setConversationSummarySubmitting(false);
    }
  }, [selectedDevice, selectedDate, timeZone, selectedTranscriptEvent, fetchConversationSummary]);

  useEffect(() => {
    if (!selectedDevice) {
      setSelectedDate(null);
      setTimelapseJob(null);
      setTimelapseMeta(null);
      setDiarySummary(null);
      previousDeviceIdRef.current = null;
      return;
    }
    const prevId = previousDeviceIdRef.current;
    const deviceChanged = Boolean(prevId && prevId !== selectedDevice.id);
    previousDeviceIdRef.current = selectedDevice.id;
    if (queryDate) {
      setSelectedDate(queryDate);
      return;
    }
    if (deviceChanged) {
      skipUrlSyncRef.current = true;
    }
    fetchLatestDate();
  }, [selectedDevice, queryDate, fetchLatestDate]);

  useEffect(() => {
    if (!selectedDate) {
      return;
    }
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const current = searchParams.get('date') ?? searchParams.get('day');
    if (current === selectedDate) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('date', selectedDate);
    nextParams.delete('day');
    setSearchParams(nextParams, { replace: true });
  }, [selectedDate, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedDate || !selectedDevice) {
      return;
    }
    fetchDailyCounts();
    fetchTimelapseStatus();
    fetchDiarySummary();
    fetchFoodSummary();
    fetchDailyEncounterPeople();
  }, [
    selectedDate,
    selectedDevice,
    fetchDailyCounts,
    fetchTimelapseStatus,
    fetchDiarySummary,
    fetchFoodSummary,
    fetchDailyEncounterPeople,
  ]);

  useEffect(() => {
    setDiarySummary(null);
    setConversationSummary(null);
    setFoodSummary(null);
    setSelectedTranscriptEventIndex(null);
    pendingSeekRef.current = null;
  }, [selectedDevice, selectedDate]);

  useEffect(() => {
    if (queryEventIndex == null) {
      if (selectedTranscriptEventIndex != null) {
        setSelectedTranscriptEventIndex(null);
        setConversationSummary(null);
      }
      return;
    }
    if (queryEventIndex >= diaryEvents.length) {
      return;
    }
    if (selectedTranscriptEventIndex !== queryEventIndex) {
      setSelectedTranscriptEventIndex(queryEventIndex);
    }
  }, [diaryEvents.length, queryEventIndex, selectedTranscriptEventIndex]);

  useEffect(() => {
    if (
      !isEventDetailMode ||
      !selectedTranscriptEvent ||
      selectedTranscriptEvent.hasData === 0 ||
      selectedTranscriptEvent.hasData === false
    ) {
      return;
    }
    fetchConversationSummary(selectedTranscriptEvent);
  }, [fetchConversationSummary, isEventDetailMode, selectedTranscriptEvent]);

  useEffect(() => {
    if (!timelapseJob || timelapseJob.status !== 'succeeded' || !selectedDevice || !selectedDate) {
      setTimelapseMeta(null);
      return;
    }
    const controller = new AbortController();
    const loadMeta = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          setTimelapseMeta(null);
          return;
        }
        const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/timelapse/metadata?day=${encodeURIComponent(
          selectedDate,
        )}&timezone=${encodeURIComponent(timeZone)}&include_frames=false&include_playback_timeline=true`;
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          setTimelapseMeta(null);
          return;
        }
        const payload = await response.json();
        const data = payload?.metadata;
        const frames = Array.isArray(data?.frames) ? data.frames : [];
        const frameCountFromData = typeof data?.frame_count === 'number' ? data.frame_count : undefined;
        const frameCount = frameCountFromData ?? (frames.length || undefined);
        const firstFrame = data?.captured_start ?? frames[0]?.captured_at;
        const lastFrame = data?.captured_end ?? (frames.length > 0 ? frames[frames.length - 1]?.captured_at : undefined);
        const frameTimes = frames
          .map((frame) => {
            const captured = frame?.captured_at;
            const ts = captured ? new Date(captured).getTime() : NaN;
            return Number.isFinite(ts) ? ts : null;
          })
          .filter((value): value is number => value !== null);
        const fps = data?.fps ?? data?.video?.fps ?? data?.output?.video?.fps;
        const width = data?.width ?? data?.video?.width ?? data?.output?.video?.width;
        const height = data?.height ?? data?.video?.height ?? data?.output?.video?.height;
        const codec = data?.codec ?? data?.video?.codec ?? data?.output?.video?.codec;
        const bitrate = data?.bitrate ?? data?.video?.bitrate ?? data?.output?.video?.bitrate;
        const durationFromData = data?.duration_sec ?? data?.video?.duration_sec ?? data?.output?.video?.duration_sec;
        const durationSec = typeof durationFromData === 'number'
          ? durationFromData
          : frameCount && fps
            ? Math.round(frameCount / fps)
            : undefined;
        const streaming = data?.streaming && typeof data.streaming === 'object' ? data.streaming as Record<string, unknown> : null;
        const playbackTimeline = normalizePlaybackTimeline(
          data?.playback_timeline_data ?? (
            data?.playback_timeline && typeof data.playback_timeline === 'object' && Array.isArray(data.playback_timeline.samples)
              ? data.playback_timeline
              : null
          ),
        );
        setTimelapseMeta({
          frameCount,
          fps: typeof fps === 'number' ? fps : undefined,
          width: typeof width === 'number' ? width : undefined,
          height: typeof height === 'number' ? height : undefined,
          codec: typeof codec === 'string' ? codec : undefined,
          bitrate: typeof bitrate === 'string' ? bitrate : undefined,
          durationSec,
          capturedStart: typeof firstFrame === 'string' ? firstFrame : undefined,
          capturedEnd: typeof lastFrame === 'string' ? lastFrame : undefined,
          frameTimes: frameTimes.length ? frameTimes : undefined,
          playbackTimeline,
          streaming: streaming
            ? {
                mode: typeof streaming.mode === 'string' ? streaming.mode : undefined,
                playbackUri: typeof streaming.playback_uri === 'string' ? streaming.playback_uri : undefined,
                masterPlaylistUri:
                  typeof streaming.master_playlist_uri === 'string' ? streaming.master_playlist_uri : undefined,
                renditions: Array.isArray(streaming.renditions) ? streaming.renditions : undefined,
              }
            : undefined,
        });
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') {
          setTimelapseMeta(null);
        }
      }
    };
    loadMeta();
    return () => controller.abort();
  }, [timelapseJob, selectedDevice, selectedDate, timeZone]);

  const handleWebSocketMessage = useCallback((message: WebSocketMessage | null | undefined) => {
    if (!message || !selectedDevice || message.device_id !== selectedDevice.id) {
      return;
    }
    const payload = message.data;
    const isTimelapseEvent = message.type === 'timelapse.completed' || message.type === 'timelapse.failed';
    const isDiaryEvent = message.type === 'diary.summary.completed' || message.type === 'diary.summary.failed';
    const isConversationEvent =
      message.type === 'diary.conversation.completed' || message.type === 'diary.conversation.failed';
    const isFoodEvent = message.type === 'diary.food.completed' || message.type === 'diary.food.failed';
    if (!isTimelapseEvent && !isDiaryEvent && !isConversationEvent && !isFoodEvent) {
      return;
    }
    if (isTimelapseEvent) {
      fetchTimelapseStatus();
    }
    if (isDiaryEvent) {
      fetchDiarySummary();
    }
    if (isFoodEvent) {
      fetchFoodSummary();
    }
    if (isConversationEvent && selectedTranscriptEvent) {
      const matchesRange =
        payload?.day === selectedDate &&
        payload?.timezone === timeZone &&
        payload?.start === selectedTranscriptEvent.start &&
        payload?.end === selectedTranscriptEvent.end;
      if (matchesRange) {
        fetchConversationSummary(selectedTranscriptEvent);
      }
    }
  }, [
    selectedDevice,
    selectedDate,
    timeZone,
    selectedTranscriptEvent,
    fetchTimelapseStatus,
    fetchDiarySummary,
    fetchFoodSummary,
    fetchConversationSummary,
  ]);

  useEffect(() => {
    if (!onWebSocketMessage) {
      return;
    }
    onWebSocketMessage(handleWebSocketMessage);
    return () => onWebSocketMessage(null);
  }, [onWebSocketMessage, handleWebSocketMessage]);

  const selectedDateValue = useMemo(
    () => (selectedDate && dayjs(selectedDate).isValid() ? dayjs(selectedDate) : null),
    [selectedDate],
  );
  const diaryHeaderTitle = useMemo(() => {
    if (!selectedDateValue) {
      return '日記';
    }
    const weekdayLabel = '日月火水木金土'[selectedDateValue.day()];
    return `過去の日記 - ${selectedDateValue.format('YYYY/M/D')}(${weekdayLabel})`;
  }, [selectedDateValue]);
  const today = dayjs().format('YYYY-MM-DD');
  const disableNext = selectedDate === today;
  const prevDate = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
  const nextDate = dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD');

  const rangeQuery = dateRange
    ? `start_time=${encodeURIComponent(dateRange.start)}&end_time=${encodeURIComponent(dateRange.end)}`
    : '';
  const refreshAll = useCallback(() => {
    fetchDailyCounts();
    void locationAnalysisDerived.loadDerivedData();
    fetchTimelapseStatus();
    fetchDiarySummary();
    fetchFoodSummary();
    fetchDailyEncounterPeople();
  }, [
    fetchDailyCounts,
    fetchDailyEncounterPeople,
    fetchDiarySummary,
    fetchFoodSummary,
    fetchTimelapseStatus,
    locationAnalysisDerived,
  ]);

  const handleHeaderDatePickerOpenChange = useCallback((open: boolean) => {
    if (open && !datePickerOpenRef.current) {
      setDatePickerPanelValue(selectedDateValue);
    }
    if (!open) {
      setDatePickerPanelValue(null);
    }
    datePickerOpenRef.current = open;
    setDatePickerOpen(open);
  }, [selectedDateValue]);

  const handleHeaderDatePickerValueChange = useCallback((date: Dayjs) => {
    setDatePickerPanelValue(date);
  }, []);

  const handleHeaderDateChange = useCallback((date: Dayjs | null) => {
    if (!date) {
      return;
    }
    setSelectedDate(date.format('YYYY-MM-DD'));
    setDatePickerPanelValue(date);
    datePickerOpenRef.current = false;
    setDatePickerOpen(false);
  }, []);

  const pageHeaderControls = useMemo(() => {
    if (!selectedDate) {
      return null;
    }
    return (
      <Space>
        <Button
          icon={<LeftOutlined />}
          onClick={() => setSelectedDate(prevDate)}
          size="small"
        />
        <DatePicker
          value={selectedDateValue}
          open={datePickerOpen}
          pickerValue={datePickerPanelValue ?? selectedDateValue}
          onPickerValueChange={handleHeaderDatePickerValueChange}
          onOpenChange={handleHeaderDatePickerOpenChange}
          onChange={handleHeaderDateChange}
          format="YYYY-MM-DD"
          allowClear={false}
          size="small"
        />
        <Button
          icon={<RightOutlined />}
          onClick={() => setSelectedDate(nextDate)}
          disabled={disableNext}
          size="small"
        />
        <Button onClick={refreshAll} loading={loading || initializing || timelapseLoading || diarySummaryLoading} size="small">
          更新
        </Button>
      </Space>
    );
  }, [
    diarySummaryLoading,
    datePickerOpen,
    datePickerPanelValue,
    disableNext,
    handleHeaderDateChange,
    handleHeaderDatePickerOpenChange,
    handleHeaderDatePickerValueChange,
    initializing,
    loading,
    nextDate,
    prevDate,
    refreshAll,
    selectedDate,
    selectedDateValue,
    timelapseLoading,
  ]);

  useEffect(() => {
    setPageHeader({
      title: diaryHeaderTitle,
      controls: pageHeaderControls,
    });
  }, [diaryHeaderTitle, pageHeaderControls, setPageHeader]);

  useEffect(() => {
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    return () => {
      if (timelapseChapterToastFadeTimerRef.current != null) {
        window.clearTimeout(timelapseChapterToastFadeTimerRef.current);
      }
      if (timelapseChapterToastHideTimerRef.current != null) {
        window.clearTimeout(timelapseChapterToastHideTimerRef.current);
      }
    };
  }, []);

  const formatDuration = (durationSec?: number) => {
    if (!durationSec || durationSec <= 0) return null;
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    return `${minutes}分${seconds.toString().padStart(2, '0')}秒`;
  };

  const syncTimelapseVideoElementSize = useCallback(() => {
    const videoEl = videoRef.current?.querySelector('video');
    if (!videoEl) {
      return;
    }
    videoEl.crossOrigin = 'use-credentials';
    Object.assign(videoEl.style, {
      display: 'block',
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    });
  }, []);

  const syncTimelapsePlaybackState = useCallback(() => {
    const videoEl = videoRef.current?.querySelector('video');
    if (!videoEl) {
      setTimelapsePlaybackState({
        currentTime: 0,
        duration: 0,
        paused: true,
        canPlay: false,
        error: false,
        playbackRate: 1,
      });
      return;
    }
    setTimelapsePlaybackState({
      currentTime: Number.isFinite(videoEl.currentTime) ? videoEl.currentTime : 0,
      duration: Number.isFinite(videoEl.duration) ? videoEl.duration : 0,
      paused: videoEl.paused,
      canPlay: videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA,
      error: Boolean(videoEl.error),
      playbackRate: Number.isFinite(videoEl.playbackRate) ? videoEl.playbackRate : 1,
    });
  }, []);

  const applyTimelapseHlsCredentials = useCallback((config: any = {}) => {
    const originalXhrSetup = config.xhrSetup;
    const originalFetchSetup = config.fetchSetup;
    return {
      ...config,
      xhrSetup: (xhr: XMLHttpRequest, url: string) => {
        xhr.withCredentials = true;
        originalXhrSetup?.(xhr, url);
      },
      fetchSetup: (context: { url: string }, initParams?: RequestInit) => {
        const nextInit = {
          ...(initParams ?? {}),
          credentials: 'include' as RequestCredentials,
        };
        if (originalFetchSetup) {
          return originalFetchSetup(context, nextInit);
        }
        return new Request(context.url, nextInit);
      },
    };
  }, []);

  const applyTimelapseHlsProviderConfig = useCallback((provider: unknown) => {
    if (!isHLSProvider(provider)) {
      return provider;
    }
    provider.library = Hls;
    provider.config = applyTimelapseHlsCredentials(provider.config);
    return provider;
  }, [applyTimelapseHlsCredentials]);

  const handleTimelapseProviderSetup = useCallback((event: Event) => {
    const provider = applyTimelapseHlsProviderConfig((event as CustomEvent<unknown>).detail);
    if (isHLSProvider(provider)) {
      provider.onInstance((instance) => {
        instance.config.xhrSetup = provider.config.xhrSetup;
        instance.config.fetchSetup = provider.config.fetchSetup;
      });
    }
    requestAnimationFrame(() => {
      syncTimelapseVideoElementSize();
    });
  }, [applyTimelapseHlsProviderConfig, syncTimelapseVideoElementSize]);

  const handleTimelapseProviderLoaderChange = useCallback((event: Event) => {
    const loader = (event as CustomEvent<unknown>).detail as {
      load?: (...args: unknown[]) => Promise<unknown>;
      preconnect?: (...args: unknown[]) => void;
      __thinkletHlsPatched?: boolean;
    } | null;
    if (!loader?.load || loader.__thinkletHlsPatched) {
      return;
    }
    loader.preconnect = () => {};
    const originalLoad = loader.load;
    loader.load = async function patchedLoad(...args: unknown[]) {
      const provider = await originalLoad.apply(this, args);
      return applyTimelapseHlsProviderConfig(provider);
    };
    loader.__thinkletHlsPatched = true;
  }, [applyTimelapseHlsProviderConfig]);

  useEffect(() => {
    if (!timelapseJob || timelapseJob.status !== 'succeeded' || !timelapseJob.video_url) {
      setTimelapseCookieReady(false);
      setTimelapseCookieLoading(false);
      setTimelapseCookieError(null);
      setTimelapseCookieExpiresAt(null);
      return;
    }
    issueTimelapseMediaCookie();
  }, [issueTimelapseMediaCookie, timelapseJob]);

  useEffect(() => {
    if (!timelapseCookieExpiresAt || !timelapseCookieReady) {
      return;
    }
    const refreshAtMs = timelapseCookieExpiresAt * 1000 - 60_000;
    const delayMs = Math.max(refreshAtMs - Date.now(), 1_000);
    const timer = window.setTimeout(() => {
      issueTimelapseMediaCookie();
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [issueTimelapseMediaCookie, timelapseCookieExpiresAt, timelapseCookieReady]);

  useEffect(() => {
    if (!timelapseJob?.video_url || !timelapseCookieReady) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      syncTimelapseVideoElementSize();
      syncTimelapsePlaybackState();
    });
    return () => cancelAnimationFrame(frameId);
  }, [syncTimelapsePlaybackState, syncTimelapseVideoElementSize, timelapseCookieReady, timelapseJob?.video_url]);

  const timelapseStatus = timelapseJob?.status ?? 'not_started';
  const timelapseRunning = ['queued', 'submitted', 'running'].includes(timelapseStatus);
  const timelapseLabel = (() => {
    switch (timelapseStatus) {
      case 'queued':
        return 'キュー待ち';
      case 'submitted':
        return '投入済み';
      case 'running':
        return '生成中';
      case 'succeeded':
        return '完了';
      case 'failed':
        return '失敗';
      default:
        return '未生成';
    }
  })();
  const timelapseButtonLabel = timelapseJob ? '再生成' : '生成';
  const timelapseVideoUrl = timelapseCookieReady ? (timelapseJob?.video_url ?? null) : null;
  useEffect(() => {
    let cancelled = false;
    let frameId = 0;
    let detach: (() => void) | null = null;

    const attachVideoListeners = () => {
      if (cancelled) {
        return;
      }
      const videoEl = videoRef.current?.querySelector('video');
      if (!videoEl) {
        frameId = requestAnimationFrame(attachVideoListeners);
        return;
      }
      const sync = () => {
        syncTimelapsePlaybackState();
      };
      const events = [
        'loadedmetadata',
        'loadeddata',
        'durationchange',
        'timeupdate',
        'play',
        'pause',
        'canplay',
        'seeking',
        'seeked',
        'ended',
        'error',
      ] as const;
      events.forEach((eventName) => videoEl.addEventListener(eventName, sync));
      sync();
      detach = () => {
        events.forEach((eventName) => videoEl.removeEventListener(eventName, sync));
      };
    };

    attachVideoListeners();
    return () => {
      cancelled = true;
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      detach?.();
    };
  }, [syncTimelapsePlaybackState, timelapseVideoUrl]);
  const videoCurrentTime = Number.isFinite(timelapsePlaybackState.currentTime)
    ? timelapsePlaybackState.currentTime
    : Number.isFinite(mediaState.currentTime)
      ? mediaState.currentTime
      : 0;
  const videoDuration =
    Number.isFinite(timelapsePlaybackState.duration) && timelapsePlaybackState.duration > 0
      ? timelapsePlaybackState.duration
      : Number.isFinite(mediaState.duration) && mediaState.duration > 0
        ? mediaState.duration
        : null;
  const timelapsePlaybackFailed = Boolean(
    timelapseVideoUrl && (timelapsePlaybackState.error || mediaState.error),
  );
  const timelapseDurationLabel = formatDuration(timelapseMeta?.durationSec);
  const timelapsePlaybackRates = useMemo(() => [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2], []);
  const formatTimelapseQualityLabel = useCallback(
    (label?: string | null, width?: number | null, height?: number | null) => {
      if ((height ?? 0) >= 1440 || (width ?? 0) >= 1920) {
        return '1080p';
      }
      if ((height ?? 0) >= 960 || (width ?? 0) >= 1280) {
        return '720p';
      }
      if (height && height > 0) {
        return '480p';
      }
      if (label) {
        return label.trim();
      }
      if (width) {
        return `${width}w`;
      }
      return 'Auto';
    },
    [],
  );
  const timelapseMetadataRenditions = useMemo(() => {
    const renditions = timelapseMeta?.streaming?.renditions;
    if (!Array.isArray(renditions)) {
      return [];
    }
    return renditions.map((rendition, index) => {
      const height = typeof rendition.height === 'number' ? rendition.height : null;
      const width = typeof rendition.width === 'number' ? rendition.width : null;
      const rawLabel = typeof rendition.label === 'string' ? rendition.label.trim() : '';
      const label = formatTimelapseQualityLabel(
        rawLabel || null,
        width,
        height,
      ) || `${index + 1}`;
      return {
        index,
        label,
        width,
        height,
      };
    });
  }, [formatTimelapseQualityLabel, timelapseMeta?.streaming?.renditions]);
  const findTimelapseMetadataRendition = useCallback(
    (width?: number | null, height?: number | null) =>
      timelapseMetadataRenditions.find(
        (rendition) => rendition.width === (width ?? null) && rendition.height === (height ?? null),
      ),
    [timelapseMetadataRenditions],
  );
  const timelapseMediaQualities = Array.isArray(mediaState.qualities) ? Array.from(mediaState.qualities) : [];
  const timelapseHlsLevels = getTimelapseHlsInstance()?.levels ?? [];
  const timelapseQualities: TimelapseQualityOption[] = (
    timelapseMediaQualities.length > 0
      ? timelapseMediaQualities.map((quality) => ({
          width: quality.width,
          height: quality.height,
          bitrate: quality.bitrate,
        }))
      : timelapseHlsLevels
  )
    .map((quality, index) => {
      const matchedLevelIndex = timelapseHlsLevels.findIndex(
        (level) => level.width === quality.width && level.height === quality.height,
      );
      const matchedLevel =
        matchedLevelIndex >= 0 ? timelapseHlsLevels[matchedLevelIndex] : timelapseHlsLevels[index];
      const matchedRendition = findTimelapseMetadataRendition(quality.width, quality.height);
      return {
        mediaIndex: index,
        levelIndex: matchedLevelIndex >= 0 ? matchedLevelIndex : index,
        label: formatTimelapseQualityLabel(
          matchedRendition?.label,
          quality.width,
          quality.height,
        ),
        width: quality.width,
        height: quality.height,
        bitrate: quality.bitrate ?? matchedLevel?.bitrate,
      };
    })
    .sort((a, b) => b.height - a.height || b.width - a.width);
  const timelapseQualityOptions: TimelapseQualityOption[] = (
    timelapseQualities.length > 0
      ? timelapseQualities
      : timelapseMetadataRenditions.map((quality) => ({
          mediaIndex: quality.index,
          levelIndex: quality.index,
          label: quality.label,
          width: quality.width ?? 0,
          height: quality.height ?? 0,
        }))
  ).sort((a, b) => b.height - a.height || b.width - a.width);
  const timelapseCurrentQualityLabel = useMemo(() => {
    if (timelapseQualitySelectionMode !== 'auto') {
      return timelapseSelectedQualityLabel;
    }
    if (!timelapseSelectedQualityLabel || timelapseSelectedQualityLabel === 'Auto') {
      return 'Auto';
    }
    return `Auto (${timelapseSelectedQualityLabel})`;
  }, [timelapseQualitySelectionMode, timelapseSelectedQualityLabel]);
  const timelapsePlaybackRate = Number.isFinite(timelapseSelectedPlaybackRate)
    ? timelapseSelectedPlaybackRate
    : 1;
  const timelapseIsFullscreen = Boolean(timelapseFullscreenHost);
  const timelapseChromeVisible = timelapseHovered || timelapseSettingsOpen;
  const timelapseSliderValue = timelapseSeekValue ?? (videoDuration ? Math.min(videoCurrentTime, videoDuration) : 0);
  const timelapsePlaybackRateLabel = useMemo(() => {
    if (Math.abs(timelapsePlaybackRate - 1) < 0.001) {
      return '標準';
    }
    return `${timelapsePlaybackRate}x`;
  }, [timelapsePlaybackRate]);
  const seekTimelapseToSecond = useCallback(
    (value: number, options: { commit?: boolean } = {}) => {
      setTimelapseSeekValue(value);
      mediaRemote.seek(value);
      const playerEl = videoRef.current;
      const canSeek =
        !!playerEl && (timelapsePlaybackState.canPlay || Boolean(videoDuration && Number.isFinite(videoDuration)));

      if (!playerEl || !canSeek) {
        pendingSeekRef.current = value;
        return;
      }

      playerEl.currentTime = value;
      const videoEl = playerEl.querySelector('video');
      if (videoEl) {
        videoEl.currentTime = value;
      }
      syncTimelapsePlaybackState();
      if (options.commit) {
        requestAnimationFrame(() => {
          setTimelapseSeekValue(null);
        });
      }
    },
    [mediaRemote, syncTimelapsePlaybackState, timelapsePlaybackState.canPlay, videoDuration],
  );
  const timelapsePlayerStyle = useMemo<React.CSSProperties & { [name: `--${string}`]: string }>(
    () => ({
      display: 'block',
      width: timelapseIsFullscreen ? 'auto' : '100%',
      height: timelapseIsFullscreen ? '100%' : 'auto',
      maxWidth: '100%',
      maxHeight: timelapseIsFullscreen ? '100%' : undefined,
      minWidth: 0,
      aspectRatio: '4 / 3',
      background:
        'radial-gradient(circle at top, rgba(51, 65, 85, 0.18), rgba(2, 6, 23, 0.94) 72%)',
      borderRadius: timelapseIsFullscreen ? 0 : 10,
      overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 14px 32px rgba(15, 23, 42, 0.22)',
      isolation: 'isolate',
    }),
    [timelapseIsFullscreen],
  );
  const timelapsePreferNativeHls = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }
    const userAgent = navigator.userAgent;
    return /Safari/i.test(userAgent) && !/Chrome|Chromium|Android/i.test(userAgent);
  }, []);
  const timelapsePlayerCss = useMemo(
    () => `
      .timelapse-player,
      .timelapse-player * {
        box-sizing: border-box;
      }

      .timelapse-player__slider .ant-slider-track {
        background: #fff !important;
      }

      .timelapse-player__slider .ant-slider-handle,
      .timelapse-player__slider .ant-slider-handle:hover,
      .timelapse-player__slider .ant-slider-handle:focus,
      .timelapse-player__slider .ant-slider-handle:focus-visible,
      .timelapse-player__slider .ant-slider-handle:active,
      .timelapse-player__slider .ant-slider-handle.ant-slider-handle-dragging {
        border: 2px solid #fff !important;
        border-radius: 50% !important;
      }

      .timelapse-player__slider .ant-slider-handle,
      .timelapse-player__slider .ant-slider-handle::before,
      .timelapse-player__slider .ant-slider-handle:hover,
      .timelapse-player__slider .ant-slider-handle:hover::before,
      .timelapse-player__slider .ant-slider-handle:focus,
      .timelapse-player__slider .ant-slider-handle:focus::before,
      .timelapse-player__slider .ant-slider-handle:focus-visible,
      .timelapse-player__slider .ant-slider-handle:focus-visible::before,
      .timelapse-player__slider .ant-slider-handle-dragging.ant-slider-handle-dragging.ant-slider-handle {
        border-color: #fff !important;
        background: #fff !important;
        box-shadow: none !important;
        outline: none !important;
        border-radius: 50% !important;
      }

      .timelapse-player__slider .ant-slider-handle-dragging.ant-slider-handle-dragging.ant-slider-handle::before {
        border-color: #fff !important;
        background: #fff !important;
        box-shadow: none !important;
        outline: none !important;
        border-radius: 50% !important;
      }

      .timelapse-player__slider .ant-slider-handle::after,
      .timelapse-player__slider .ant-slider-handle:hover::after,
      .timelapse-player__slider .ant-slider-handle:focus::after,
      .timelapse-player__slider .ant-slider-handle:focus-visible::after,
      .timelapse-player__slider .ant-slider-handle-dragging.ant-slider-handle-dragging.ant-slider-handle::after {
        background: #fff !important;
        border-color: #fff !important;
        border: 2px solid #fff !important;
        box-shadow: none !important;
        outline: none !important;
        border-radius: 50% !important;
      }

      .timelapse-player__slider .ant-slider-handle::before,
      .timelapse-player__slider .ant-slider-handle:hover::before,
      .timelapse-player__slider .ant-slider-handle:focus::before,
      .timelapse-player__slider .ant-slider-handle:focus-visible::before {
        border-radius: 50% !important;
      }
    `,
    [],
  );
  const timelapseTopBadgeStyle = useMemo<React.CSSProperties>(
    () => ({
      marginInlineEnd: 0,
      borderRadius: 999,
      paddingInline: 10,
      border: '1px solid rgba(255, 255, 255, 0.16)',
      backdropFilter: 'blur(14px)',
      fontWeight: 600,
    }),
    [],
  );
  const timelapseSettingsPanelStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'fixed',
      zIndex: 10000,
      width: 'min(296px, calc(100% - 24px))',
      maxWidth: 'calc(100% - 24px)',
      maxHeight: 'min(352px, calc(100vh - 24px))',
      background: 'rgba(15, 23, 42, 0.88)',
      backdropFilter: 'blur(24px)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 16,
      boxShadow: '0 22px 48px rgba(2, 6, 23, 0.48)',
      padding: '12px 14px',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      overflowY: 'hidden',
    }),
    [],
  );

  const handleTimelapseSettingsOpenChange = useCallback((open: boolean) => {
    setTimelapseSettingsOpen(open);
    if (!open) {
      setTimelapseSettingsView('root');
    }
  }, []);

  const updateTimelapseSettingsPanelOffset = useCallback(() => {
    const buttonEl = timelapseSettingsButtonRef.current;
    if (!buttonEl || typeof window === 'undefined') {
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const horizontalPadding = 12;
    const verticalGap = 12;
    const panelWidth = Math.min(296, Math.max(viewportWidth - 24, 0));
    const left = Math.min(
      Math.max(rect.right - panelWidth, horizontalPadding),
      Math.max(viewportWidth - panelWidth - horizontalPadding, horizontalPadding),
    );
    const availableAbove = Math.max(rect.top - verticalGap - horizontalPadding, 0);
    const availableBelow = Math.max(viewportHeight - rect.bottom - verticalGap - horizontalPadding, 0);
    const prefersAbove = availableAbove >= 180 || availableAbove >= availableBelow;
    const maxHeight = Math.max(
      Math.min(352, prefersAbove ? availableAbove : availableBelow, viewportHeight - 24),
      208,
    );

    if (prefersAbove) {
      setTimelapseSettingsPanelOffset({
        left,
        bottom: Math.max(viewportHeight - rect.top + verticalGap, horizontalPadding),
        maxHeight,
      });
      return;
    }

    setTimelapseSettingsPanelOffset({
      left,
      top: Math.min(rect.bottom + verticalGap, viewportHeight - horizontalPadding),
      maxHeight,
    });
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenEl = document.fullscreenElement;
      const containerEl = timelapseContainerRef.current;
      if (fullscreenEl instanceof HTMLElement && containerEl?.contains(fullscreenEl)) {
        setTimelapseFullscreenHost(fullscreenEl);
        setTimelapseHovered(true);
        return;
      }
      setTimelapseFullscreenHost(null);
    };
    handleFullscreenChange();
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!timelapseSettingsOpen) {
      return undefined;
    }
    updateTimelapseSettingsPanelOffset();
    const handlePositionUpdate = () => updateTimelapseSettingsPanelOffset();
    window.addEventListener('resize', handlePositionUpdate);
    window.addEventListener('scroll', handlePositionUpdate, true);
    document.addEventListener('fullscreenchange', handlePositionUpdate);
    return () => {
      window.removeEventListener('resize', handlePositionUpdate);
      window.removeEventListener('scroll', handlePositionUpdate, true);
      document.removeEventListener('fullscreenchange', handlePositionUpdate);
    };
  }, [timelapseSettingsOpen, updateTimelapseSettingsPanelOffset]);

  useEffect(() => {
    if (!timelapseSettingsOpen) {
      return undefined;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (timelapseSettingsPanelRef.current?.contains(target)) {
        return;
      }
      if (timelapseSettingsButtonRef.current?.contains(target)) {
        return;
      }
      handleTimelapseSettingsOpenChange(false);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [handleTimelapseSettingsOpenChange, timelapseSettingsOpen]);

  const handleTimelapseSeek = useCallback((value: number) => {
    seekTimelapseToSecond(value);
  }, [seekTimelapseToSecond]);

  const handleTimelapseSeekCommit = useCallback((value: number) => {
    seekTimelapseToSecond(value, { commit: true });
  }, [seekTimelapseToSecond]);

  const findTimelapseQualityCollectionItem = useCallback(
    (selectedQuality: TimelapseQualityOption) => {
      const qualityCollection = getTimelapseQualityCollection();
      if (!qualityCollection) {
        return null;
      }
      for (let index = 0; index < qualityCollection.length; index += 1) {
        const item = qualityCollection[index];
        if (item?.width === selectedQuality.width && item?.height === selectedQuality.height) {
          return item;
        }
      }
      if (selectedQuality.mediaIndex >= 0 && selectedQuality.mediaIndex < qualityCollection.length) {
        return qualityCollection[selectedQuality.mediaIndex];
      }
      return null;
    },
    [getTimelapseQualityCollection],
  );

  const handleTimelapseQualityChange = useCallback((selectedQuality: TimelapseQualityOption) => {
    const qualityItem = findTimelapseQualityCollectionItem(selectedQuality);
    if (qualityItem) {
      qualityItem.selected = true;
    }
    const hls = getTimelapseHlsInstance();
    if (hls) {
      hls.currentLevel = selectedQuality.levelIndex;
      hls.nextLevel = selectedQuality.levelIndex;
      hls.loadLevel = selectedQuality.levelIndex;
    }
    setTimelapseSelectedQualityLabel(selectedQuality?.label ?? 'Auto');
    setTimelapseQualitySelectionMode('manual');
  }, [findTimelapseQualityCollectionItem, getTimelapseHlsInstance]);

  const handleTimelapseAutoQuality = useCallback(() => {
    const qualityCollection = getTimelapseQualityCollection();
    if (qualityCollection?.autoSelect) {
      qualityCollection.autoSelect();
    }
    const hls = getTimelapseHlsInstance();
    if (hls) {
      hls.currentLevel = -1;
      hls.nextLevel = -1;
      hls.loadLevel = -1;
    }
    setTimelapseSelectedQualityLabel('Auto');
    setTimelapseQualitySelectionMode('auto');
  }, [getTimelapseHlsInstance, getTimelapseQualityCollection]);

  const getTimelapseResolvedAutoQualityLabel = useCallback(() => {
    const selectedQuality = mediaState.quality;
    if (selectedQuality?.width || selectedQuality?.height) {
      return formatTimelapseQualityLabel(undefined, selectedQuality.width, selectedQuality.height);
    }
    const hls = getTimelapseHlsInstance();
    if (!hls) {
      return null;
    }
    const currentLevelIndex = hls.currentLevel >= 0 ? hls.currentLevel : hls.nextLoadLevel;
    const currentLevel = currentLevelIndex >= 0 ? hls.levels[currentLevelIndex] : null;
    if (!currentLevel) {
      return null;
    }
    const matchedOption = timelapseQualityOptions.find((quality) => quality.levelIndex === currentLevelIndex);
    return matchedOption?.label
      ?? formatTimelapseQualityLabel(undefined, currentLevel.width, currentLevel.height);
  }, [formatTimelapseQualityLabel, getTimelapseHlsInstance, mediaState.quality, timelapseQualityOptions]);

  useEffect(() => {
    const hls = getTimelapseHlsInstance();
    const qualityList = Array.isArray(mediaState.qualities) ? mediaState.qualities : [];
    if (!qualityList.length) {
      if (!hls) {
        return;
      }
      if (hls.autoLevelEnabled || hls.manualLevel < 0) {
        const resolvedAutoQualityLabel = getTimelapseResolvedAutoQualityLabel() ?? 'Auto';
        if (
          timelapseQualitySelectionMode !== 'auto'
          || timelapseSelectedQualityLabel !== resolvedAutoQualityLabel
        ) {
          setTimelapseQualitySelectionMode('auto');
          setTimelapseSelectedQualityLabel(resolvedAutoQualityLabel);
        }
        return;
      }
      const selectedLevel = hls.levels[hls.manualLevel];
      const selectedQuality = timelapseQualityOptions.find((quality) => quality.levelIndex === hls.manualLevel);
      const selectedQualityLabel = selectedQuality?.label
        ?? formatTimelapseQualityLabel(undefined, selectedLevel?.width, selectedLevel?.height);
      if (
        timelapseQualitySelectionMode !== 'manual' ||
        timelapseSelectedQualityLabel !== selectedQualityLabel
      ) {
        setTimelapseQualitySelectionMode('manual');
        setTimelapseSelectedQualityLabel(selectedQualityLabel);
      }
      return;
    }
    if (mediaState.autoQuality) {
      const resolvedAutoQualityLabel = getTimelapseResolvedAutoQualityLabel() ?? 'Auto';
      if (
        timelapseQualitySelectionMode !== 'auto'
        || timelapseSelectedQualityLabel !== resolvedAutoQualityLabel
      ) {
        setTimelapseQualitySelectionMode('auto');
        setTimelapseSelectedQualityLabel(resolvedAutoQualityLabel);
      }
      return;
    }
    const selectedQuality = mediaState.quality;
    if (!selectedQuality) {
      return;
    }
    const matchedOption = timelapseQualityOptions.find(
      (quality) => quality.width === selectedQuality.width && quality.height === selectedQuality.height,
    );
    const selectedQualityLabel =
      matchedOption?.label
      ?? formatTimelapseQualityLabel(undefined, selectedQuality.width, selectedQuality.height);
    if (
      timelapseQualitySelectionMode !== 'manual' ||
      timelapseSelectedQualityLabel !== selectedQualityLabel
    ) {
      setTimelapseQualitySelectionMode('manual');
      setTimelapseSelectedQualityLabel(selectedQualityLabel);
    }
  }, [
    formatTimelapseQualityLabel,
    getTimelapseResolvedAutoQualityLabel,
    getTimelapseHlsInstance,
    mediaState.autoQuality,
    mediaState.qualities,
    mediaState.quality,
    timelapseQualityOptions,
    timelapseQualitySelectionMode,
    timelapseSelectedQualityLabel,
  ]);

  const handleTimelapseTogglePlayPause = useCallback(async () => {
    const videoEl = videoRef.current?.querySelector('video');
    if (!videoEl) {
      return;
    }
    try {
      if (videoEl.paused) {
        await videoEl.play();
      } else {
        videoEl.pause();
      }
      syncTimelapsePlaybackState();
    } catch (error) {
      secureLogger.warn('Failed to toggle timelapse playback', error);
    }
  }, [syncTimelapsePlaybackState]);

  const handleTimelapsePlaybackRateChange = useCallback((rate: number) => {
    const playerEl = videoRef.current;
    if (!playerEl) {
      return;
    }
    playerEl.playbackRate = rate;
    const videoEl = playerEl.querySelector('video');
    if (videoEl) {
      videoEl.playbackRate = rate;
    }
    setTimelapseSelectedPlaybackRate(rate);
    syncTimelapsePlaybackState();
  }, [syncTimelapsePlaybackState]);

  const handleTimelapseToggleFullscreen = useCallback(async () => {
    const containerEl = timelapseContainerRef.current;
    if (!containerEl) {
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      await containerEl.requestFullscreen();
    } catch (error) {
      secureLogger.warn('Failed to enter timelapse container fullscreen, falling back to media fullscreen', error);
      const playerEl = videoRef.current;
      if (!playerEl) {
        return;
      }
      try {
        await playerEl.enterFullscreen('prefer-media');
        return;
      } catch (mediaFullscreenError) {
        secureLogger.warn('Failed to enter media fullscreen, falling back to element fullscreen', mediaFullscreenError);
      }
      const videoEl = playerEl.querySelector('video');
      if (videoEl?.requestFullscreen) {
        try {
          await videoEl.requestFullscreen();
        } catch (fallbackError) {
          secureLogger.warn('Fallback fullscreen request failed', fallbackError);
        }
      }
    }
  }, []);

  const formatPlaybackClock = useCallback((seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const secs = safeSeconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const renderTimelapseMenuItem = useCallback((
    label: string,
    description: string,
    selected: boolean,
    onClick: () => void,
    disabled = false,
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        border: 0,
        borderRadius: 12,
        background: selected ? 'rgba(255,255,255,0.12)' : 'transparent',
        color: disabled ? 'rgba(255,255,255,0.45)' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 11, color: disabled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.62)' }}>
          {description}
        </span>
      </span>
      <span style={{ width: 18, display: 'flex', justifyContent: 'center', color: '#fff' }}>
        {selected ? <CheckOutlined /> : null}
      </span>
    </button>
  ), []);

  const timelapseMenuPanelStyle: React.CSSProperties = useMemo(
    () => ({
      width: '100%',
      maxWidth: '100%',
      color: '#fff',
      display: 'grid',
      gap: 8,
      minWidth: 0,
      boxSizing: 'border-box',
      overflowX: 'hidden',
    }),
    [],
  );

  const timelapseMenuSectionLabelStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      color: 'rgba(255,255,255,0.56)',
      textTransform: 'uppercase',
      paddingInline: 4,
    }),
    [],
  );

  const timelapseMenuChevronStyle: React.CSSProperties = useMemo(
    () => ({
      color: 'rgba(255,255,255,0.72)',
      fontSize: 12,
    }),
    [],
  );

  const renderTimelapseMenuLinkRow = useCallback(
    (label: string, value: string, onClick: () => void) => (
      <button
        type="button"
        onClick={onClick}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 14px',
          border: 0,
          borderRadius: 14,
          background: 'rgba(255,255,255,0.06)',
          color: '#fff',
          cursor: 'pointer',
          textAlign: 'left',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
          <span
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.64)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </span>
        </span>
        <RightOutlined style={{ ...timelapseMenuChevronStyle, flexShrink: 0 }} />
      </button>
    ),
    [timelapseMenuChevronStyle],
  );

  const timelapseSettingsContent = useMemo(() => {
    const renderSubmenuHeader = (title: string) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingBottom: 4,
          marginBottom: 4,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <button
          type="button"
          onClick={() => setTimelapseSettingsView('root')}
          style={{
            width: 28,
            height: 28,
            border: 0,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
          aria-label={`${title}メニューへ戻る`}
        >
          <LeftOutlined style={{ fontSize: 12 }} />
        </button>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{title}</span>
      </div>
    );

    if (timelapseSettingsView === 'quality') {
      return (
        <div style={timelapseMenuPanelStyle}>
          {renderSubmenuHeader('画質')}
          <div style={timelapseMenuSectionLabelStyle}>Adaptive Bitrate</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {renderTimelapseMenuItem('Auto', '通信状況に合わせて自動で切替', timelapseQualitySelectionMode === 'auto', () => {
              handleTimelapseAutoQuality();
              setTimelapseSettingsOpen(false);
              setTimelapseSettingsView('root');
            })}
          </div>
          <div style={timelapseMenuSectionLabelStyle}>固定画質</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {timelapseQualityOptions.map((quality) =>
              renderTimelapseMenuItem(
                quality.label,
                '固定画質で再生',
                timelapseQualitySelectionMode === 'manual' && timelapseSelectedQualityLabel === quality.label,
                () => {
                  handleTimelapseQualityChange(quality);
                  setTimelapseSettingsOpen(false);
                  setTimelapseSettingsView('root');
                },
                !mediaState.canSetQuality,
              ),
            )}
            {timelapseQualityOptions.length === 0 && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.62)', padding: '6px 4px' }}>
                rendition 情報の読み込み待ち
              </div>
            )}
            {timelapseQualityOptions.length > 0 && !mediaState.canSetQuality && (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', padding: '2px 4px 0' }}>
                再生開始後に画質固定を有効化
              </div>
            )}
          </div>
        </div>
      );
    }

    if (timelapseSettingsView === 'speed') {
      return (
        <div style={timelapseMenuPanelStyle}>
          {renderSubmenuHeader('再生速度')}
          <div style={timelapseMenuSectionLabelStyle}>Playback</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {timelapsePlaybackRates.map((rate) =>
              renderTimelapseMenuItem(
                rate === 1 ? '標準' : `${rate}x`,
                rate === 1 ? '通常速度で再生' : `${rate} 倍速で再生`,
                Math.abs(timelapseSelectedPlaybackRate - rate) < 0.001,
                () => {
                  handleTimelapsePlaybackRateChange(rate);
                  setTimelapseSettingsOpen(false);
                  setTimelapseSettingsView('root');
                },
              ),
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={timelapseMenuPanelStyle}>
        <div style={timelapseMenuSectionLabelStyle}>設定</div>
        <div style={{ display: 'grid', gap: 6 }}>
          {renderTimelapseMenuLinkRow('画質', timelapseCurrentQualityLabel, () => setTimelapseSettingsView('quality'))}
          {renderTimelapseMenuLinkRow('再生速度', timelapsePlaybackRateLabel, () => setTimelapseSettingsView('speed'))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'rgba(255,255,255,0.06)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>ループ再生</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.64)' }}>最後まで行ったら自動で先頭へ戻す</div>
            </div>
            <Switch size="small" checked={timelapseLoopEnabled} onChange={setTimelapseLoopEnabled} />
          </div>
        </div>
      </div>
    );
  }, [
    handleTimelapseAutoQuality,
    handleTimelapsePlaybackRateChange,
    handleTimelapseQualityChange,
    mediaState.canSetQuality,
    renderTimelapseMenuItem,
    renderTimelapseMenuLinkRow,
    timelapseCurrentQualityLabel,
    timelapseLoopEnabled,
    timelapseMenuPanelStyle,
    timelapseMenuSectionLabelStyle,
    timelapsePlaybackRate,
    timelapsePlaybackRateLabel,
    timelapsePlaybackRates,
    timelapseQualitySelectionMode,
    timelapseQualityOptions,
    timelapseSelectedPlaybackRate,
    timelapseSelectedQualityLabel,
    timelapseSettingsView,
  ]);
  const timelapseSettingsPortalTarget =
    typeof document === 'undefined' ? null : timelapseFullscreenHost ?? document.body;

  const diaryStatus = diarySummary?.status ?? 'not_started';
  const diaryRunning = ['queued', 'running'].includes(diaryStatus);
  const diaryLabel = (() => {
    switch (diaryStatus) {
      case 'queued':
        return 'キュー待ち';
      case 'running':
        return '生成中';
      case 'succeeded':
        return '完了';
      case 'failed':
        return '失敗';
      default:
        return '未生成';
    }
  })();
  const diaryButtonLabel = diarySummary ? '再生成' : '生成';
  const geminiUsage = diarySummary?.input_stats?.gemini_usage;
  const promptTotalTokens = geminiUsage?.promptTokenCount;
  const totalTokens = geminiUsage?.totalTokenCount;
  const thoughtsTokens = geminiUsage?.thoughtsTokenCount;
  const outputTotalTokens =
    totalTokens !== undefined && promptTotalTokens !== undefined
      ? Math.max(totalTokens - promptTotalTokens, 0)
      : undefined;
  const outputTokens =
    outputTotalTokens !== undefined
      ? thoughtsTokens !== undefined
        ? Math.max(outputTotalTokens - thoughtsTokens, 0)
        : outputTotalTokens
      : undefined;
  const textTokens = geminiUsage?.promptTokensDetails?.find(
    (detail) => detail?.modality?.toUpperCase() === 'TEXT',
  )?.tokenCount;
  const videoTokens = geminiUsage?.promptTokensDetails?.find(
    (detail) => detail?.modality?.toUpperCase() === 'VIDEO',
  )?.tokenCount;
  const diaryEmptyLabel = diaryRunning
    ? 'サマリー生成中'
    : diaryStatus === 'failed'
      ? 'サマリー生成失敗'
      : 'サマリー未生成';
  const conversationStatus = conversationSummary?.status ?? 'not_started';
  const conversationRunning = ['queued', 'running'].includes(conversationStatus);
  const conversationButtonLabel = conversationSummary ? '再生成' : '生成';
  const conversationPayload = conversationSummary?.summary ?? null;
  const conversationTopicSummaries = useMemo(() => {
    if (!conversationPayload || typeof conversationPayload !== 'object') {
      return null;
    }
    const payload = conversationPayload as Record<string, unknown>;
    const summaries = payload.summaries;
    if (!Array.isArray(summaries)) {
      return null;
    }
    const parsed = summaries
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const entry = item as Record<string, unknown>;
        const topic =
          (typeof entry.topic === 'string' && entry.topic) ||
          (typeof entry.title === 'string' && entry.title) ||
          '話題';
        const summary =
          (typeof entry.summary === 'string' && entry.summary) ||
          (typeof entry.text === 'string' && entry.text) ||
          null;
        const ainote =
          (typeof entry.ainote === 'string' && entry.ainote.trim()) ||
          (typeof entry.ai_note === 'string' && entry.ai_note.trim()) ||
          null;
        if (!summary) {
          return null;
        }
        return { topic, summary, ainote };
      })
      .filter((item): item is { topic: string; summary: string; ainote: string | null } => Boolean(item));
    return parsed.length ? parsed : null;
  }, [conversationPayload]);
  const conversationSummaryText = useMemo(() => {
    if (!conversationPayload) {
      return null;
    }
    if (typeof conversationPayload === 'string') {
      return conversationPayload;
    }
    if (typeof conversationPayload === 'object') {
      const payload = conversationPayload as Record<string, unknown>;
      if (typeof payload.text === 'string') {
        return payload.text;
      }
    }
    return JSON.stringify(conversationPayload, null, 2);
  }, [conversationPayload]);
  const conversationTodos = useMemo(() => {
    if (!conversationPayload || typeof conversationPayload !== 'object') {
      return null;
    }
    const payload = conversationPayload as Record<string, unknown>;
    const todoValue = payload.todo;
    if (!todoValue) {
      return null;
    }
    if (Array.isArray(todoValue)) {
      const items = todoValue
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0);
      return items.length ? items : null;
    }
    if (typeof todoValue === 'string' && todoValue.trim()) {
      return [todoValue.trim()];
    }
    return null;
  }, [conversationPayload]);
  const conversationEmptyLabel = !selectedTranscriptEvent
    ? 'イベントを選択してください'
    : conversationRunning
      ? '会話要約生成中'
      : conversationStatus === 'failed'
        ? '会話要約生成失敗'
        : '会話要約未生成';
  const foodStatus = foodSummary?.status ?? 'not_started';
  const foodRunning = ['queued', 'running'].includes(foodStatus);
  const foodLabel = (() => {
    switch (foodStatus) {
      case 'queued':
        return 'キュー待ち';
      case 'running':
        return '生成中';
      case 'succeeded':
        return '完了';
      case 'failed':
        return '失敗';
      default:
        return '未生成';
    }
  })();
  const foodButtonLabel = foodSummary ? '再生成' : '生成';
  const foodPayload = foodSummary?.summary ?? null;
  const foodUsage =
    (foodSummary?.input_stats as { gemini_usage?: GeminiUsageMetadata } | null | undefined)?.gemini_usage;
  const foodPromptTokens = foodUsage?.promptTokenCount;
  const foodTotalTokens = foodUsage?.totalTokenCount;
  const foodThoughtsTokens = foodUsage?.thoughtsTokenCount;
  const foodOutputTotalTokens =
    foodTotalTokens !== undefined && foodPromptTokens !== undefined
      ? Math.max(foodTotalTokens - foodPromptTokens, 0)
      : undefined;
  const foodOutputTokens =
    foodOutputTotalTokens !== undefined
      ? foodThoughtsTokens !== undefined
        ? Math.max(foodOutputTotalTokens - foodThoughtsTokens, 0)
        : foodOutputTotalTokens
      : undefined;
  const foodTextTokens = foodUsage?.promptTokensDetails?.find(
    (detail) => detail?.modality?.toUpperCase() === 'TEXT',
  )?.tokenCount;
  const foodVideoTokens = foodUsage?.promptTokensDetails?.find(
    (detail) => detail?.modality?.toUpperCase() === 'VIDEO',
  )?.tokenCount;
  const foodItems = useMemo(() => {
    if (!foodPayload || typeof foodPayload !== 'object') {
      return [] as FoodSummaryItem[];
    }
    const payload = foodPayload as Record<string, unknown>;
    const entries = payload.drinksandeats;
    if (!Array.isArray(entries)) {
      return [] as FoodSummaryItem[];
    }
    return entries
      .map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null;
        }
        const item = entry as Record<string, unknown>;
        const start = typeof item.start === 'string' ? item.start : undefined;
        const place = typeof item.place === 'string' ? item.place : undefined;
        const contents = typeof item.contents === 'string' ? item.contents : undefined;
        const calory = typeof item.calory === 'string' ? item.calory : undefined;
        if (!start && !place && !contents && !calory) {
          return null;
        }
        return { start, place, contents, calory } as FoodSummaryItem;
      })
      .filter((entry): entry is FoodSummaryItem => Boolean(entry));
  }, [foodPayload]);
  const foodSummaryText = useMemo(() => {
    if (!foodPayload) {
      return null;
    }
    if (typeof foodPayload === 'string') {
      return foodPayload;
    }
    if (typeof foodPayload === 'object') {
      return JSON.stringify(foodPayload, null, 2);
    }
    return null;
  }, [foodPayload]);
  const foodEmptyLabel = foodRunning
    ? '飲食生成中'
    : foodStatus === 'failed'
      ? '飲食生成失敗'
      : '飲食未生成';
  const selectedEventHasData =
    selectedTranscriptEvent?.hasData !== 0 && selectedTranscriptEvent?.hasData !== false;
  const summaryPaneTitleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    minHeight: 32,
    marginBottom: 12,
  };
  const summaryRegenerateButtonStyle: React.CSSProperties = {
    fontSize: 10,
    height: 24,
    paddingInline: 8,
    lineHeight: '22px',
  };
  const diarySectionPadding = 16;
  const diarySectionGap = 16;
  const summaryViewportBottomOffset = diarySectionPadding + diarySectionGap / 2;
  const diarySectionStyle: React.CSSProperties = {
    background: '#fff',
    padding: diarySectionPadding,
    minWidth: 0,
  };
  const diarySectionTitleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
    marginBottom: 12,
  };
  const renderPeopleCards = useCallback(
    (people: DailyEncounterPerson[], emptyDescription: string) => {
      if (dailyEncounterPeopleLoading) {
        return (
          <div style={{ padding: '12px 0' }}>
            <Spin />
          </div>
        );
      }
      if (!people.length) {
        return <Empty description={emptyDescription} />;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {people.map((person) => (
            <div
              key={person.person_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #f0f0f0',
                background: '#fff',
              }}
            >
              <Avatar
                src={person.representative_thumbnail_url || person.thumbnail_url || undefined}
                size={44}
                style={{ flex: '0 0 auto', background: '#e6f4ff', color: '#1677ff' }}
              >
                {person.display_name.slice(0, 1)}
              </Avatar>
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text strong style={{ display: 'block' }}>
                  {person.display_name}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  顔検出 {person.face_count} 件
                  {person.first_seen_at && (
                    <> / {dayjs(person.first_seen_at).format('HH:mm')}</>
                  )}
                  {person.last_seen_at && person.last_seen_at !== person.first_seen_at && (
                    <> - {dayjs(person.last_seen_at).format('HH:mm')}</>
                  )}
                </Text>
              </div>
            </div>
          ))}
        </div>
      );
    },
    [dailyEncounterPeopleLoading],
  );

  const effectiveVideoDuration = videoDuration ?? timelapseMeta?.durationSec ?? null;
  const playbackTimeline = timelapseMeta?.playbackTimeline ?? null;
  const hasPlaybackTimeline = Boolean(playbackTimeline);
  const playbackTimezone = playbackTimeline?.timezone || JST_TIMEZONE;
  const selectedDayTimeBounds = useMemo(() => {
    if (!selectedDate) {
      return null;
    }
    const startMoment = dayjs.tz(`${selectedDate}T00:00:00`, playbackTimezone);
    const endMoment = startMoment.add(1, 'day');
    if (!startMoment.isValid() || !endMoment.isValid()) {
      return null;
    }
    return {
      startMs: startMoment.valueOf(),
      endMs: endMoment.valueOf(),
    };
  }, [playbackTimezone, selectedDate]);
  const dayLocationTimelineItems = useMemo(() => {
    if (!selectedDayTimeBounds) {
      return locationAnalysisDerived.timelineItems;
    }
    return locationAnalysisDerived.timelineItems.flatMap((item) => {
      const startMs = parseMeasuredAt(item.started_at)?.getTime();
      const endMs = parseMeasuredAt(item.ended_at)?.getTime();
      if (
        startMs == null ||
        endMs == null ||
        !Number.isFinite(startMs) ||
        !Number.isFinite(endMs)
      ) {
        return [];
      }
      if (startMs >= selectedDayTimeBounds.endMs || endMs <= selectedDayTimeBounds.startMs) {
        return [];
      }
      const clippedStartedAt = new Date(Math.max(startMs, selectedDayTimeBounds.startMs)).toISOString();
      const clippedEndedAt = new Date(Math.min(endMs, selectedDayTimeBounds.endMs)).toISOString();
      if (item.started_at === clippedStartedAt && item.ended_at === clippedEndedAt) {
        return [item];
      }
      if (item.type === 'staypoint') {
        return [{
          ...item,
          started_at: clippedStartedAt,
          ended_at: clippedEndedAt,
          staypoint: {
            ...item.staypoint,
            started_at: clippedStartedAt,
            ended_at: clippedEndedAt,
          },
        }];
      }
      return [{
        ...item,
        started_at: clippedStartedAt,
        ended_at: clippedEndedAt,
        tripleg: {
          ...item.tripleg,
          started_at: clippedStartedAt,
          ended_at: clippedEndedAt,
        },
      }];
    });
  }, [locationAnalysisDerived.timelineItems, selectedDayTimeBounds]);
  const timelapseHighlightSecond = timelapseSeekValue ?? videoCurrentTime;
  const eventTimeBounds = useMemo(() => {
    if (!selectedDate) {
      return diaryEvents.map(() => ({ startMs: null, endMs: null }));
    }
    return diaryEvents.map((event) => {
      const startMoment = dayjs.tz(`${selectedDate}T${event.start}:00`, playbackTimezone);
      const endMoment = dayjs.tz(`${selectedDate}T${event.end}:00`, playbackTimezone);
      if (!startMoment.isValid() || !endMoment.isValid()) {
        return { startMs: null, endMs: null };
      }
      return { startMs: startMoment.valueOf(), endMs: endMoment.valueOf() };
    });
  }, [diaryEvents, playbackTimezone, selectedDate]);
  const selectedEventPeople = useMemo(() => {
    if (selectedTranscriptEventIndex == null) {
      return [];
    }
    const selectedBounds = eventTimeBounds[selectedTranscriptEventIndex];
    if (!selectedBounds || selectedBounds.startMs == null || selectedBounds.endMs == null) {
      return [];
    }
    return dailyEncounterPeople.filter((person) => {
      const firstSeenMs = person.first_seen_at ? dayjs(person.first_seen_at).valueOf() : null;
      const lastSeenMs = person.last_seen_at ? dayjs(person.last_seen_at).valueOf() : firstSeenMs;
      if (
        firstSeenMs == null ||
        lastSeenMs == null ||
        !Number.isFinite(firstSeenMs) ||
        !Number.isFinite(lastSeenMs)
      ) {
        return false;
      }
      return firstSeenMs <= selectedBounds.endMs && lastSeenMs >= selectedBounds.startMs;
    });
  }, [dailyEncounterPeople, eventTimeBounds, selectedTranscriptEventIndex]);
  const eventMapFocusTargets = useMemo<EventMapFocusTarget[]>(() => {
    if (!isEventDetailMode || selectedTranscriptEventIndex == null) {
      return [];
    }
    const selectedBounds = eventTimeBounds[selectedTranscriptEventIndex];
    if (
      !selectedBounds ||
      selectedBounds.startMs == null ||
      selectedBounds.endMs == null ||
      selectedBounds.endMs <= selectedBounds.startMs
    ) {
      return [];
    }

    const eventDurationMs = selectedBounds.endMs - selectedBounds.startMs;
    const minAbsoluteOverlapMs = Math.min(5 * 60 * 1000, Math.max(60 * 1000, eventDurationMs * 0.2));
    const targetByKey = new Map<string, EventMapFocusTarget>();
    const addTarget = (target: EventMapFocusTarget) => {
      targetByKey.set(`${target.type}:${target.id}`, target);
    };
    const hasMeaningfulOverlap = (startedAt?: string | null, endedAt?: string | null) => {
      const startMs = parseMeasuredAt(startedAt)?.getTime();
      const endMs = parseMeasuredAt(endedAt)?.getTime();
      if (
        startMs == null ||
        endMs == null ||
        !Number.isFinite(startMs) ||
        !Number.isFinite(endMs) ||
        endMs <= startMs
      ) {
        return false;
      }
      const overlapMs = Math.min(endMs, selectedBounds.endMs as number) - Math.max(startMs, selectedBounds.startMs as number);
      if (overlapMs <= 0) {
        return false;
      }
      const objectDurationMs = endMs - startMs;
      return (
        overlapMs >= minAbsoluteOverlapMs ||
        overlapMs / eventDurationMs >= 0.4 ||
        overlapMs / objectDurationMs >= 0.5
      );
    };

    locationAnalysisDerived.staypoints.forEach((staypoint) => {
      if (!hasMeaningfulOverlap(staypoint.started_at, staypoint.ended_at)) {
        return;
      }
      addTarget({ type: 'staypoint', id: staypoint.id });
      if (staypoint.effective_location_id) {
        addTarget({ type: 'location', id: staypoint.effective_location_id });
      }
    });
    locationAnalysisDerived.triplegs.forEach((tripleg) => {
      if (!hasMeaningfulOverlap(tripleg.started_at, tripleg.ended_at)) {
        return;
      }
      addTarget({ type: 'tripleg', id: tripleg.id });
    });

    return Array.from(targetByKey.values());
  }, [
    eventTimeBounds,
    isEventDetailMode,
    locationAnalysisDerived.staypoints,
    locationAnalysisDerived.triplegs,
    selectedTranscriptEventIndex,
  ]);
  const eventMapFocusEnabled = isEventDetailMode && eventMapFocusTargets.length > 0;
  const eventOffsets = useMemo(() => {
    if (!selectedDate || !effectiveVideoDuration || !playbackTimeline) {
      return diaryEvents.map(() => ({ start: null, end: null }));
    }
    return eventTimeBounds.map(({ startMs, endMs }) => ({
      start: resolvePlaybackSecondAtTimeMs(playbackTimeline, startMs),
      end: resolvePlaybackSecondAtTimeMs(playbackTimeline, endMs),
    }));
  }, [diaryEvents, selectedDate, effectiveVideoDuration, playbackTimeline, eventTimeBounds]);
  const timelapseChapters = useMemo<TimelapseChapter[]>(() => {
    if (!effectiveVideoDuration || effectiveVideoDuration <= 0) {
      return [];
    }
    return diaryEvents.flatMap((event, eventIndex) => {
      const hasData = event.hasData !== 0 && event.hasData !== false;
      const offset = eventOffsets[eventIndex];
      if (!hasData || offset?.start == null || offset.end == null) {
        return [];
      }
      const startSec = Math.max(0, Math.min(offset.start, effectiveVideoDuration));
      const endSec = Math.max(startSec, Math.min(offset.end, effectiveVideoDuration));
      if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) {
        return [];
      }
      return [{
        eventIndex,
        title: event.title || `${event.start}-${event.end}`,
        startLabel: event.start,
        endLabel: event.end,
        startSec,
        endSec,
      }];
    });
  }, [diaryEvents, effectiveVideoDuration, eventOffsets]);

  const timelapseFrameTimeMs = useMemo(() => {
    if (!timelapseMeta || !effectiveVideoDuration || !Number.isFinite(timelapseHighlightSecond)) {
      return null;
    }
    return resolveTimeMsAtPlaybackSecond(playbackTimeline, timelapseHighlightSecond);
  }, [timelapseMeta, effectiveVideoDuration, playbackTimeline, timelapseHighlightSecond]);

  const timelapsePlaybackPosition = useMemo(
    () => resolvePositionAtPlaybackSecond(playbackTimeline, timelapseHighlightSecond),
    [playbackTimeline, timelapseHighlightSecond],
  );
  const playbackTimeControlsHighlight = timelapseFrameTimeMs != null && Number.isFinite(timelapseFrameTimeMs);
  const playbackHighlightActive = playbackTimeControlsHighlight && !timelapsePlaybackState.paused;
  const activeLocationTimelineTarget = useMemo<DerivedTimelineTarget | null>(() => {
    if (!playbackTimeControlsHighlight) {
      return null;
    }
    const frameTimeMs = timelapseFrameTimeMs as number;
    const activeItem = dayLocationTimelineItems.find((item) => {
      const startMs = parseMeasuredAt(item.started_at)?.getTime();
      const endMs = parseMeasuredAt(item.ended_at)?.getTime();
      if (startMs == null || endMs == null || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        return false;
      }
      return frameTimeMs >= startMs && frameTimeMs <= endMs;
    });
    if (activeItem) {
      return { type: activeItem.type, id: activeItem.id };
    }
    const activeStaypoint = locationAnalysisDerived.staypoints.find((staypoint) => {
      const startMs = parseMeasuredAt(staypoint.started_at)?.getTime();
      const endMs = parseMeasuredAt(staypoint.ended_at)?.getTime();
      if (startMs == null || endMs == null || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        return false;
      }
      return frameTimeMs >= startMs && frameTimeMs <= endMs;
    });
    if (activeStaypoint) {
      const visibleStaypointItem = dayLocationTimelineItems.find(
        (item) =>
          item.type === 'staypoint' &&
          (item.id === activeStaypoint.id ||
            (frameTimeMs >= (parseMeasuredAt(item.started_at)?.getTime() ?? Number.POSITIVE_INFINITY) &&
              frameTimeMs <= (parseMeasuredAt(item.ended_at)?.getTime() ?? Number.NEGATIVE_INFINITY))),
      );
      return { type: 'staypoint', id: visibleStaypointItem?.id ?? activeStaypoint.id };
    }
    const activeTripleg = locationAnalysisDerived.triplegs.find((tripleg) => {
      const startMs = parseMeasuredAt(tripleg.started_at)?.getTime();
      const endMs = parseMeasuredAt(tripleg.ended_at)?.getTime();
      if (startMs == null || endMs == null || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        return false;
      }
      return frameTimeMs >= startMs && frameTimeMs <= endMs;
    });
    if (activeTripleg) {
      return { type: 'tripleg', id: activeTripleg.id };
    }
    return null;
  }, [
    dayLocationTimelineItems,
    locationAnalysisDerived.staypoints,
    locationAnalysisDerived.triplegs,
    playbackTimeControlsHighlight,
    timelapseFrameTimeMs,
  ]);
  const effectiveLocationTimelineTarget = playbackHighlightActive
    ? activeLocationTimelineTarget ?? null
    : locationAnalysisDerived.selectedDerivedTarget;

  const activeDiaryIndex = useMemo(() => {
    if (!playbackHighlightActive || !eventOffsets.length || !effectiveVideoDuration) {
      return null;
    }
    if (timelapseFrameTimeMs != null && Number.isFinite(timelapseFrameTimeMs)) {
      const idx = eventTimeBounds.findIndex(({ startMs, endMs }) => {
        if (startMs == null || endMs == null) {
          return false;
        }
        return timelapseFrameTimeMs >= startMs && timelapseFrameTimeMs <= endMs;
      });
      return idx === -1 ? null : idx;
    }
    return null;
  }, [eventOffsets, effectiveVideoDuration, eventTimeBounds, playbackHighlightActive, timelapseFrameTimeMs]);
  const activeTimelapseChapterIndex = useMemo(() => {
    if (!timelapseChapters.length || !Number.isFinite(timelapseHighlightSecond)) {
      return null;
    }
    const activeIndex = timelapseChapters.findIndex((chapter, chapterIndex) => {
      const isLastChapter = chapterIndex === timelapseChapters.length - 1;
      return timelapseHighlightSecond >= chapter.startSec &&
        (timelapseHighlightSecond < chapter.endSec || isLastChapter);
    });
    return activeIndex >= 0 ? activeIndex : null;
  }, [timelapseChapters, timelapseHighlightSecond]);
  const selectedActivityTimelineItem = useMemo(() => {
    const selectedTarget = locationAnalysisDerived.selectedDerivedTarget;
    if (!selectedTarget) {
      return null;
    }
    return dayLocationTimelineItems.find(
      (item) => item.type === selectedTarget.type && item.id === selectedTarget.id,
    ) ?? null;
  }, [dayLocationTimelineItems, locationAnalysisDerived.selectedDerivedTarget]);
  const selectedActivityTimeRange = useMemo(() => {
    if (!selectedActivityTimelineItem) {
      return null;
    }
    const startMs = parseMeasuredAt(selectedActivityTimelineItem.started_at)?.getTime();
    const endMs = parseMeasuredAt(selectedActivityTimelineItem.ended_at)?.getTime();
    if (
      startMs == null ||
      endMs == null ||
      !Number.isFinite(startMs) ||
      !Number.isFinite(endMs) ||
      endMs <= startMs
    ) {
      return null;
    }
    return { startMs, endMs };
  }, [selectedActivityTimelineItem]);
  const selectedActivityDayScaleRange = useMemo(() => {
    if (!selectedDate || !selectedActivityTimeRange) {
      return null;
    }
    const dayStartMs = dayjs.tz(`${selectedDate}T00:00:00`, playbackTimezone).valueOf();
    const dayEndMs = dayjs.tz(`${selectedDate}T00:00:00`, playbackTimezone).add(1, 'day').valueOf();
    if (!Number.isFinite(dayStartMs) || !Number.isFinite(dayEndMs) || dayEndMs <= dayStartMs) {
      return null;
    }
    const clampedStartMs = Math.max(selectedActivityTimeRange.startMs, dayStartMs);
    const clampedEndMs = Math.min(selectedActivityTimeRange.endMs, dayEndMs);
    if (clampedEndMs <= clampedStartMs) {
      return null;
    }
    const durationMs = dayEndMs - dayStartMs;
    return {
      startPct: ((clampedStartMs - dayStartMs) / durationMs) * 100,
      endPct: ((clampedEndMs - dayStartMs) / durationMs) * 100,
    };
  }, [playbackTimezone, selectedActivityTimeRange, selectedDate]);
  const activityScaleTicks = useMemo(
    () => Array.from({ length: 25 }, (_, hour) => ({ hour, label: `${hour}:00` })),
    [],
  );
  const eventOverlapsSelectedActivity = useCallback(
    (index: number) => {
      if (!selectedActivityTimeRange) {
        return true;
      }
      const eventBounds = eventTimeBounds[index];
      if (!eventBounds || eventBounds.startMs == null || eventBounds.endMs == null) {
        return false;
      }
      return eventBounds.startMs < selectedActivityTimeRange.endMs &&
        eventBounds.endMs > selectedActivityTimeRange.startMs;
    },
    [eventTimeBounds, selectedActivityTimeRange],
  );

  const handleDiaryEventClick = useCallback(
    (index: number) => {
      const event = diaryEvents[index];
      if (event) {
        setSelectedTranscriptEventIndex(index);
      }
      const offset = eventOffsets[index]?.start;
      if (offset != null) {
        seekTimelapseToSecond(offset, { commit: true });
      }
      const nextParams = new URLSearchParams(searchParams);
      if (selectedDate) {
        nextParams.set('date', selectedDate);
      }
      nextParams.delete('day');
      nextParams.set('event', String(index));
      setSearchParams(nextParams);
    },
    [diaryEvents, eventOffsets, searchParams, seekTimelapseToSecond, selectedDate, setSearchParams],
  );
  const handleTimelapseSeekbarClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!effectiveVideoDuration || effectiveVideoDuration <= 0) {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect.width) {
        return;
      }
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      seekTimelapseToSecond(ratio * effectiveVideoDuration, { commit: true });
    },
    [effectiveVideoDuration, seekTimelapseToSecond],
  );
  const showTimelapseChapterToast = useCallback((title: string) => {
    if (timelapseChapterToastFadeTimerRef.current != null) {
      window.clearTimeout(timelapseChapterToastFadeTimerRef.current);
    }
    if (timelapseChapterToastHideTimerRef.current != null) {
      window.clearTimeout(timelapseChapterToastHideTimerRef.current);
    }
    setTimelapseChapterToast({ title, visible: true });
    timelapseChapterToastFadeTimerRef.current = window.setTimeout(() => {
      setTimelapseChapterToast((current) => current ? { ...current, visible: false } : current);
    }, 1000);
    timelapseChapterToastHideTimerRef.current = window.setTimeout(() => {
      setTimelapseChapterToast(null);
    }, 1300);
  }, []);
  const handleTimelapseChapterClick = useCallback(
    (startSec: number, title?: string) => {
      seekTimelapseToSecond(startSec, { commit: true });
      if (title) {
        showTimelapseChapterToast(title);
      }
    },
    [seekTimelapseToSecond, showTimelapseChapterToast],
  );
  const handleTimelapseChapterSkip = useCallback(
    (direction: 'previous' | 'next') => {
      if (!timelapseChapters.length) {
        return;
      }
      const seekEpsilonSec = 0.25;
      const targetIndex = direction === 'next'
        ? timelapseChapters.findIndex((chapter) => chapter.startSec > timelapseHighlightSecond + seekEpsilonSec)
        : (() => {
            for (let index = timelapseChapters.length - 1; index >= 0; index -= 1) {
              if (timelapseChapters[index].startSec < timelapseHighlightSecond - seekEpsilonSec) {
                return index;
              }
            }
            return -1;
          })();
      const target = timelapseChapters[targetIndex];
      if (!target) {
        return;
      }
      handleTimelapseChapterClick(target.startSec, target.title);
    },
    [
      handleTimelapseChapterClick,
      timelapseChapters,
      timelapseHighlightSecond,
    ],
  );
  const handleEventDetailBack = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('event');
    setSearchParams(nextParams);
    setSelectedTranscriptEventIndex(null);
    setConversationSummary(null);
  }, [searchParams, setSearchParams]);

  const handleLocationTimelineSelect = useCallback(
    (target: DerivedTimelineTarget) => {
      locationAnalysisDerived.setSelectedDerivedTarget(target);
      const item = dayLocationTimelineItems.find(
        (row) => row.type === target.type && row.id === target.id,
      );
      if (!item) {
        return;
      }
      const startMs = parseMeasuredAt(item.started_at)?.getTime();
      if (startMs == null) {
        return;
      }
      const offset = resolvePlaybackSecondAtTimeMs(playbackTimeline, startMs);
      if (offset == null) {
        return;
      }
      seekTimelapseToSecond(offset, { commit: true });
    },
    [dayLocationTimelineItems, locationAnalysisDerived, playbackTimeline, seekTimelapseToSecond],
  );
  const selectedSummaryActivityTarget = locationAnalysisDerived.selectedDerivedTarget;
  const setSelectedSummaryActivityTarget = locationAnalysisDerived.setSelectedDerivedTarget;

  useEffect(() => {
    pendingSeekRef.current = null;
    setTimelapseSeekValue(null);
    setTimelapseSelectedQualityLabel('Auto');
    setTimelapseQualitySelectionMode('auto');
    setTimelapseSelectedPlaybackRate(1);
    setTimelapseSettingsOpen(false);
    setTimelapseSettingsView('root');
  }, [timelapseVideoUrl]);

  useEffect(() => {
    if (timelapseSeekValue == null) {
      return;
    }
    if (Math.abs(videoCurrentTime - timelapseSeekValue) < 0.25) {
      setTimelapseSeekValue(null);
    }
  }, [timelapseSeekValue, videoCurrentTime]);

  useEffect(() => {
    if (!Number.isFinite(timelapsePlaybackState.playbackRate)) {
      return;
    }
    if (Math.abs(timelapseSelectedPlaybackRate - timelapsePlaybackState.playbackRate) < 0.001) {
      return;
    }
    setTimelapseSelectedPlaybackRate(timelapsePlaybackState.playbackRate);
  }, [timelapsePlaybackState.playbackRate, timelapseSelectedPlaybackRate]);

  useEffect(() => {
    if (pendingSeekRef.current == null || !videoRef.current) {
      return;
    }
    if (!timelapsePlaybackState.canPlay && !(videoDuration && Number.isFinite(videoDuration))) {
      return;
    }
    videoRef.current.currentTime = pendingSeekRef.current;
    pendingSeekRef.current = null;
  }, [timelapsePlaybackState.canPlay, timelapseVideoUrl, videoDuration]);

  useEffect(() => {
    if (!selectedSummaryActivityTarget) {
      return undefined;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const activityRoot = summaryActivityRef.current;
      const timelineItem = target.closest('.location-analysis-timeline-item');
      if (activityRoot && timelineItem && activityRoot.contains(timelineItem)) {
        return;
      }
      setSelectedSummaryActivityTarget(null);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [selectedSummaryActivityTarget, setSelectedSummaryActivityTarget]);

  useEffect(() => {
    const scheduleUpdate = () => {
      requestAnimationFrame(() => {
        const viewportTop = summaryViewportRef.current?.getBoundingClientRect().top ?? 0;
        const nextHeight = Math.floor(window.innerHeight - viewportTop - summaryViewportBottomOffset);
        if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
          setSummaryMaxHeight(null);
          return;
        }
        setSummaryMaxHeight(Math.max(480, nextHeight));
      });
    };
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [selectedDate, timelapseVideoUrl, diarySummary?.updated_at, summaryViewportBottomOffset]);

  useEffect(() => {
    const updateMapHeight = () => {
      const width = timelapseLocationMapElement?.getBoundingClientRect().width ?? 0;
      if (!Number.isFinite(width) || width <= 0) {
        return;
      }
      setTimelapseLocationMapHeight(Math.max(240, Math.round(width)));
    };

    updateMapHeight();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => updateMapHeight());
      if (timelapseLocationMapElement) {
        observer.observe(timelapseLocationMapElement);
      }
    } else {
      window.addEventListener('resize', updateMapHeight);
    }
    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener('resize', updateMapHeight);
      }
    };
  }, [timelapseLocationMapElement]);

  useEffect(() => {
    setConversationSummary(null);
    if (queryEventIndex == null) {
      setSelectedTranscriptEventIndex(null);
    }
  }, [diarySummary?.updated_at, queryEventIndex]);

  if (!selectedDevice) {
    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="デバイスを選択してください" />
    );
  }

  if (!selectedDate) {
    return (
      <div style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ background: '#f5f5f5', margin: -24, minHeight: 'calc(100% + 48px)' }}>
      <Spin spinning={loading || initializing || timelapseLoading || diarySummaryLoading}>
        <Row gutter={[0, diarySectionGap]}>
          <Col span={24}>
            <div style={diarySectionStyle}>
              <Row ref={summaryViewportRef} gutter={[16, 16]} style={{ alignItems: 'stretch' }}>
                <Col xs={24} lg={6}>
                  <div
                    ref={summaryLeftRef}
                    style={{
                      minWidth: 0,
                      height: summaryMaxHeight ? `${summaryMaxHeight}px` : undefined,
                      minHeight: 0,
                      overflowY: summaryMaxHeight ? 'auto' : undefined,
                      paddingRight: summaryMaxHeight ? 4 : undefined,
                    }}
                  >
                    <div style={summaryPaneTitleRowStyle}>
                      <div>
                        <Text strong>タイムラプス</Text>
                        <Text>: {timelapseLabel}</Text>
                        {timelapseJob?.updated_at && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            更新: {dayjs(timelapseJob.updated_at).format('HH:mm')}
                          </Text>
                        )}
                        {timelapseJob?.error_message && (
                          <div style={{ color: '#c41d7f', marginTop: 4 }}>
                            {timelapseJob.error_message}
                          </div>
                        )}
                      </div>
                      <Button
                        type="primary"
                        onClick={requestTimelapse}
                        disabled={timelapseSubmitting || timelapseRunning}
                        loading={timelapseSubmitting}
                        style={summaryRegenerateButtonStyle}
                      >
                        {timelapseButtonLabel}
                      </Button>
                    </div>
                    {timelapseVideoUrl ? (
                      <div
                        ref={timelapseContainerRef}
                        style={{
                          width: '100%',
                          minWidth: 0,
                          overflow: timelapseIsFullscreen ? 'hidden' : 'visible',
                          position: 'relative',
                          display: timelapseIsFullscreen ? 'flex' : 'block',
                          alignItems: timelapseIsFullscreen ? 'center' : undefined,
                          justifyContent: timelapseIsFullscreen ? 'center' : undefined,
                          height: timelapseIsFullscreen ? '100%' : undefined,
                          background: timelapseIsFullscreen ? '#020617' : undefined,
                        }}
                        onMouseEnter={() => setTimelapseHovered(true)}
                        onMouseLeave={() => setTimelapseHovered(false)}
                      >
                        <style>{timelapsePlayerCss}</style>
                        <MediaPlayer
                          key={timelapseVideoUrl}
                          ref={videoRef}
                          className="timelapse-player"
                          src={{ src: timelapseVideoUrl, type: resolveTimelapseMediaType(timelapseVideoUrl) }}
                          controls={false}
                          preload="metadata"
                          loop={timelapseLoopEnabled}
                          viewType="video"
                          preferNativeHLS={timelapsePreferNativeHls}
                          onProviderLoaderChange={handleTimelapseProviderLoaderChange}
                          onProviderSetup={handleTimelapseProviderSetup}
                          style={timelapsePlayerStyle as React.CSSProperties & { [name: `--${string}`]: string }}
                        >
                          <MediaOutlet style={{ display: 'block', width: '100%', height: '100%' }} />
                          {timelapseChapterToast && (
                            <div
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                zIndex: 4,
                                transform: timelapseChapterToast.visible
                                  ? 'translate(-50%, -50%) scale(1)'
                                  : 'translate(-50%, -50%) scale(0.98)',
                                opacity: timelapseChapterToast.visible ? 1 : 0,
                                transition: 'opacity 300ms ease, transform 300ms ease',
                                pointerEvents: 'none',
                                maxWidth: '78%',
                                padding: '10px 16px',
                                borderRadius: 12,
                                background: 'rgba(15, 23, 42, 0.72)',
                                color: '#fff',
                                fontSize: 15,
                                fontWeight: 700,
                                lineHeight: 1.35,
                                textAlign: 'center',
                                textShadow: '0 1px 2px rgba(0,0,0,0.34)',
                                boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
                                backdropFilter: 'blur(10px)',
                              }}
                            >
                              {timelapseChapterToast.title}
                            </div>
                          )}
                          <div
                            style={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              zIndex: 3,
                              display: 'flex',
                              gap: 8,
                              alignItems: 'center',
                              opacity: timelapseChromeVisible ? 1 : 0,
                              transform: timelapseChromeVisible ? 'translateY(0)' : 'translateY(-4px)',
                              transition: 'opacity 180ms ease, transform 180ms ease',
                              pointerEvents: timelapseChromeVisible ? 'auto' : 'none',
                            }}
                          >
                            {timelapseLoopEnabled && (
                              <Tag
                                color="rgba(34, 197, 94, 0.2)"
                                style={{
                                  ...timelapseTopBadgeStyle,
                                  color: '#dcfce7',
                                  border: '1px solid rgba(134, 239, 172, 0.28)',
                                }}
                              >
                                LOOP
                              </Tag>
                            )}
                          </div>
                          <div
                            style={{
                              position: 'absolute',
                              insetInline: 0,
                              bottom: 0,
                              zIndex: 3,
                              padding: '18px 16px 14px',
                              background:
                                'linear-gradient(180deg, rgba(2, 6, 23, 0) 0%, rgba(2, 6, 23, 0.72) 42%, rgba(2, 6, 23, 0.92) 100%)',
                              color: '#fff',
                              opacity: timelapseChromeVisible ? 1 : 0,
                              transform: timelapseChromeVisible ? 'translateY(0)' : 'translateY(8px)',
                              transition: 'opacity 180ms ease, transform 180ms ease',
                              pointerEvents: timelapseChromeVisible ? 'auto' : 'none',
                            }}
                          >
                            <div style={{ marginBottom: 12 }}>
                              {timelapseChapters.length > 0 && effectiveVideoDuration ? (
                                <div
                                  role="slider"
                                  aria-label="タイムラプス チャプターシーク"
                                  aria-valuemin={0}
                                  aria-valuemax={Math.round(effectiveVideoDuration)}
                                  aria-valuenow={Math.round(timelapseSliderValue)}
                                  tabIndex={0}
                                  onClick={handleTimelapseSeekbarClick}
                                  onKeyDown={(event) => {
                                    if (!effectiveVideoDuration) {
                                      return;
                                    }
                                    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                                      event.preventDefault();
                                      const delta = event.key === 'ArrowLeft' ? -5 : 5;
                                      seekTimelapseToSecond(
                                        Math.max(0, Math.min(effectiveVideoDuration, timelapseSliderValue + delta)),
                                        { commit: true },
                                      );
                                    }
                                  }}
                                  onMouseLeave={() => setHoveredTimelapseChapterIndex(null)}
                                  style={{
                                    position: 'relative',
                                    height: 28,
                                    cursor: 'pointer',
                                    outline: 'none',
                                  }}
                                >
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      right: 0,
                                      top: 12,
                                      height: 5,
                                      borderRadius: 999,
                                      background: 'rgba(255,255,255,0.24)',
                                      overflow: 'hidden',
                                    }}
                                  />
                                  {timelapseChapters.map((chapter, chapterIndex) => {
                                    const startPct = (chapter.startSec / effectiveVideoDuration) * 100;
                                    const endPct = (chapter.endSec / effectiveVideoDuration) * 100;
                                    const isActive = chapterIndex === activeTimelapseChapterIndex;
                                    const isHovered = chapterIndex === hoveredTimelapseChapterIndex;
                                    const isPast = chapter.endSec <= timelapseSliderValue;
                                    const chapterLabel = `${chapter.startLabel}-${chapter.endLabel} ${chapter.title}`;
                                    return (
                                      <button
                                        key={`${chapter.eventIndex}-${chapter.startSec}-${chapter.endSec}`}
                                        type="button"
                                        title={chapterLabel}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleTimelapseChapterClick(chapter.startSec, chapter.title);
                                        }}
                                        onMouseEnter={() => setHoveredTimelapseChapterIndex(chapterIndex)}
                                        onFocus={() => setHoveredTimelapseChapterIndex(chapterIndex)}
                                        onBlur={() => setHoveredTimelapseChapterIndex(null)}
                                        style={{
                                          position: 'absolute',
                                          left: `${startPct}%`,
                                          right: `${Math.max(0, 100 - endPct)}%`,
                                          top: 8,
                                          minWidth: 3,
                                          height: 13,
                                          border: 0,
                                          borderRadius: 2,
                                          padding: 0,
                                          marginLeft: 1,
                                          marginRight: 1,
                                          background: isActive
                                            ? '#60a5fa'
                                            : isHovered
                                              ? 'rgba(255,255,255,0.86)'
                                              : isPast
                                                ? '#fff'
                                                : 'rgba(255,255,255,0.42)',
                                          boxShadow: isActive ? '0 0 0 2px rgba(96, 165, 250, 0.45)' : 'none',
                                          cursor: 'pointer',
                                          transition: 'background 120ms ease, box-shadow 120ms ease',
                                        }}
                                        aria-label={`チャプターへ移動: ${chapterLabel}`}
                                      >
                                        {isHovered && (
                                          <span
                                            style={{
                                              position: 'absolute',
                                              left: '50%',
                                              bottom: 22,
                                              transform: 'translateX(-50%)',
                                              maxWidth: 240,
                                              minWidth: 120,
                                              padding: '6px 8px',
                                              borderRadius: 6,
                                              background: 'rgba(15, 23, 42, 0.96)',
                                              color: '#fff',
                                              fontSize: 11,
                                              lineHeight: 1.35,
                                              whiteSpace: 'normal',
                                              textAlign: 'left',
                                              pointerEvents: 'none',
                                              boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
                                            }}
                                          >
                                            <span style={{ display: 'block', fontWeight: 700 }}>{chapter.title}</span>
                                            <span style={{ display: 'block', opacity: 0.76 }}>
                                              {chapter.startLabel} - {chapter.endLabel}
                                            </span>
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: `${Math.max(0, Math.min(100, (timelapseSliderValue / effectiveVideoDuration) * 100))}%`,
                                      top: 5,
                                      width: 3,
                                      height: 19,
                                      borderRadius: 999,
                                      background: '#fff',
                                      boxShadow: '0 0 0 2px rgba(15, 23, 42, 0.35)',
                                      transform: 'translateX(-50%)',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </div>
                              ) : (
                                <Slider
                                  className="timelapse-player__slider"
                                  min={0}
                                  max={videoDuration ?? 0}
                                  step={0.1}
                                  value={timelapseSliderValue}
                                  onChange={(value) => handleTimelapseSeek(Array.isArray(value) ? value[0] : value)}
                                  onChangeComplete={(value) =>
                                    handleTimelapseSeekCommit(Array.isArray(value) ? value[0] : value)
                                  }
                                  tooltip={{
                                    formatter: (value) =>
                                      typeof value === 'number' ? formatPlaybackClock(value) : '',
                                  }}
                                  styles={{
                                    track: { background: '#fff' },
                                    rail: { background: 'rgba(255,255,255,0.26)' },
                                    handle: { borderColor: '#fff', background: '#fff', boxShadow: 'none', outline: 'none' },
                                  }}
                                />
                              )}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Space size={10} wrap>
                                <button
                                  type="button"
                                  onClick={handleTimelapseTogglePlayPause}
                                  style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    background: 'rgba(255, 255, 255, 0.14)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                  }}
                                  aria-label={timelapsePlaybackState.paused ? '再生' : '一時停止'}
                                >
                                  {timelapsePlaybackState.paused ? (
                                    <span
                                      style={{
                                        display: 'block',
                                        width: 0,
                                        height: 0,
                                        marginLeft: 3,
                                        borderTop: '8px solid transparent',
                                        borderBottom: '8px solid transparent',
                                        borderLeft: '14px solid #fff',
                                      }}
                                    />
                                  ) : (
                                    <span style={{ display: 'flex', gap: 4 }}>
                                      <span style={{ display: 'block', width: 4, height: 16, borderRadius: 999, background: '#fff' }} />
                                      <span style={{ display: 'block', width: 4, height: 16, borderRadius: 999, background: '#fff' }} />
                                    </span>
                                  )}
                                </button>
                                <Text style={{ color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                                  {formatPlaybackClock(videoCurrentTime)}
                                  {' / '}
                                  {formatPlaybackClock(videoDuration ?? 0)}
                                </Text>
                              </Space>
                              <Space size={10} wrap>
                                {timelapseChapters.length > 0 && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleTimelapseChapterSkip('previous')}
                                      style={{
                                        width: 36,
                                        height: 36,
                                        border: 0,
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                      aria-label="前のチャプターへ"
                                    >
                                      <LeftOutlined style={{ color: '#fff', fontSize: 16 }} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleTimelapseChapterSkip('next')}
                                      style={{
                                        width: 36,
                                        height: 36,
                                        border: 0,
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        padding: 0,
                                      }}
                                      aria-label="次のチャプターへ"
                                    >
                                      <RightOutlined style={{ color: '#fff', fontSize: 16 }} />
                                    </button>
                                  </>
                                )}
                                <button
                                  ref={timelapseSettingsButtonRef}
                                  type="button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleTimelapseSettingsOpenChange(!timelapseSettingsOpen);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key !== 'Enter' && event.key !== ' ') {
                                      return;
                                    }
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleTimelapseSettingsOpenChange(!timelapseSettingsOpen);
                                  }}
                                  style={{
                                    width: 44,
                                    height: 44,
                                    border: 0,
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                  }}
                                  aria-label="設定"
                                >
                                  <SettingOutlined style={{ color: '#fff', fontSize: 22 }} />
                                </button>
                                  <button
                                    type="button"
                                    onClick={handleTimelapseToggleFullscreen}
                                    style={{
                                      width: 44,
                                      height: 44,
                                      border: 0,
                                      borderRadius: '50%',
                                      background: 'rgba(255, 255, 255, 0.08)',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                  }}
                                  aria-label="全画面"
                                >
                                  <FullscreenOutlined style={{ color: '#fff', fontSize: 22 }} />
                                </button>
                              </Space>
                            </div>
                          </div>
                        </MediaPlayer>
                        {timelapseSettingsOpen && timelapseSettingsPortalTarget && timelapseSettingsPanelOffset
                          ? createPortal(
                              <div
                                ref={timelapseSettingsPanelRef}
                                style={{
                                  ...timelapseSettingsPanelStyle,
                                  left: timelapseSettingsPanelOffset.left,
                                  top: timelapseSettingsPanelOffset.top,
                                  bottom: timelapseSettingsPanelOffset.bottom,
                                  maxHeight: timelapseSettingsPanelOffset.maxHeight,
                                }}
                              >
                                {timelapseSettingsContent}
                              </div>,
                              timelapseSettingsPortalTarget,
                            )
                          : null}
                        {timelapsePlaybackFailed && (
                          <div style={{ color: '#cf1322', marginTop: 8 }}>
                            timelapse のストリーミング再生に失敗
                          </div>
                        )}
                        {timelapseMeta && !hasPlaybackTimeline && (
                          <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 12 }}
                            message="時刻対応メタデータがありません"
                            description="このタイムラプスでは、動画再生位置と実時刻の対応が取得できません。イベントの自動ハイライトやイベントクリックによる動画シークは無効です。"
                          />
                        )}
                      </div>
                    ) : timelapseCookieLoading ? (
                      <div style={{ paddingBlock: 24 }}>
                        <Spin size="small" /> <Text style={{ marginLeft: 8 }}>視聴権限を取得中</Text>
                      </div>
                    ) : timelapseCookieError ? (
                      <div style={{ color: '#cf1322' }}>{timelapseCookieError}</div>
                    ) : (
                      <Empty description={timelapseRunning ? 'タイムラプス生成中' : 'タイムラプス未生成'} />
                    )}
                    {timelapseMeta && (
                      <div style={{ marginTop: 12 }}>
                        <Text type="secondary">メタ情報</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 4, fontSize: 12, color: '#666' }}>
                          {timelapseMeta.frameCount !== undefined && (
                            <span>フレーム: {timelapseMeta.frameCount}</span>
                          )}
                          {timelapseMeta.fps !== undefined && (
                            <span>FPS: {timelapseMeta.fps}</span>
                          )}
                          {timelapseMeta.width !== undefined && timelapseMeta.height !== undefined && (
                            <span>解像度: {timelapseMeta.width}x{timelapseMeta.height}</span>
                          )}
                          {timelapseMeta.codec && (
                            <span>Codec: {timelapseMeta.codec}</span>
                          )}
                          {timelapseMeta.bitrate && (
                            <span>Bitrate: {timelapseMeta.bitrate}</span>
                          )}
                          {timelapseDurationLabel && (
                            <span>時間: {timelapseDurationLabel}</span>
                          )}
                          <span>
                            時刻対応: {hasPlaybackTimeline ? `${playbackTimeline?.samples.length ?? 0} 点` : 'なし'}
                          </span>
                          {timelapseMeta.capturedStart && timelapseMeta.capturedEnd && (
                            <span>
                              範囲: {dayjs(timelapseMeta.capturedStart).format('HH:mm')} - {dayjs(timelapseMeta.capturedEnd).format('HH:mm')}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary">現在地</Text>
                      {isEventDetailMode && selectedTranscriptEvent && (
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {eventMapFocusEnabled
                              ? 'イベントに対応する移動・滞在区間を強調表示しています'
                              : 'このイベントに対応する移動・滞在区間は特定できません'}
                          </Text>
                        </div>
                      )}
                      <div ref={setTimelapseLocationMapElement} style={{ marginTop: 6 }}>
                        <Spin spinning={locationAnalysisDerived.loading}>
                          <LocationAnalysisMap
                            title="Timelapse location"
                            staypoints={locationAnalysisDerived.staypoints}
                            triplegs={locationAnalysisDerived.triplegs}
                            trips={locationAnalysisDerived.trips}
                            locations={locationAnalysisDerived.visibleLocations}
                            locationExtentBufferM={locationAnalysisDerived.locationExtentBufferM}
                            playbackPosition={timelapsePlaybackPosition}
                            focusedDerivedTargets={eventMapFocusTargets}
                            dimUnfocusedDerivedTargets={eventMapFocusEnabled}
                            height={timelapseLocationMapHeight}
                          />
                        </Spin>
                      </div>
                    </div>
                  </div>
                </Col>
                {isEventDetailMode && selectedTranscriptEvent ? (
                <Col xs={24} lg={18}>
                  <div
                    style={{
                      height: summaryMaxHeight ? `${summaryMaxHeight}px` : undefined,
                      minHeight: 0,
                      overflowY: summaryMaxHeight ? 'auto' : undefined,
                      paddingRight: summaryMaxHeight ? 4 : undefined,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 16,
                        padding: '8px 10px',
                        marginBottom: 12,
                        background: '#f6faff',
                        border: '1px solid #d6e4ff',
                      }}
                    >
                      <Button size="small" icon={<LeftOutlined />} onClick={handleEventDetailBack}>
                        イベント一覧に戻る
                      </Button>
                      <Text strong style={{ fontSize: 12 }}>
                        {selectedTranscriptEvent.start} - {selectedTranscriptEvent.end} {selectedTranscriptEvent.title}
                      </Text>
                    </div>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <div>
                        <Text strong style={{ fontSize: 18 }}>
                          {selectedTranscriptEvent.title}
                        </Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                            <Tag color="blue">
                              {selectedTranscriptEvent.start} - {selectedTranscriptEvent.end}
                            </Tag>
                            {selectedEventHasData ? (
                              <Tag color="green">データあり</Tag>
                            ) : (
                              <Tag>データ無し</Tag>
                            )}
                          </Space>
                        </div>
                      </div>
                      <div style={{ padding: 12, border: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <Text strong>イベント概要</Text>
                        <div style={{ marginTop: 8 }}>
                          <Text style={{ whiteSpace: 'pre-wrap' }}>
                            {selectedTranscriptEvent.detail || '概要はまだありません'}
                          </Text>
                        </div>
                      </div>
                      <div style={{ padding: 12, border: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                          <Text strong>登場人物</Text>
                          {!dailyEncounterPeopleLoading && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {selectedEventPeople.length}人
                            </Text>
                          )}
                        </div>
                        <div style={{ marginTop: 10 }}>
                          {renderPeopleCards(selectedEventPeople, 'このイベントの登場人物はまだありません')}
                        </div>
                      </div>
                      <div style={{ padding: 12, border: '1px solid #f0f0f0', background: '#fff' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 16,
                            marginBottom: 10,
                          }}
                        >
                          <Text strong>会話要約</Text>
                          {selectedEventHasData && (
                            <Button
                              size="small"
                              onClick={requestConversationSummary}
                              disabled={conversationRunning}
                              loading={conversationSummarySubmitting}
                            >
                              {conversationButtonLabel}
                            </Button>
                          )}
                        </div>
                        <Spin spinning={conversationSummaryLoading || conversationSummarySubmitting}>
                          {!selectedEventHasData ? (
                            <Empty description="このイベントに会話データはありません" />
                          ) : conversationRunning ? (
                            <Empty description="会話要約生成中" />
                          ) : conversationStatus === 'failed' ? (
                            <Alert
                              type="warning"
                              showIcon
                              message="会話要約生成失敗"
                              description={conversationSummary?.error_message || undefined}
                            />
                          ) : conversationTopicSummaries ? (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                              {conversationTopicSummaries.map((topic, index) => (
                                <div
                                  key={`${topic.topic}-${index}`}
                                  style={{ paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}
                                >
                                  <Text strong>{topic.topic}</Text>
                                  <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{topic.summary}</div>
                                  {topic.ainote && (
                                    <Alert
                                      type="info"
                                      showIcon
                                      message="AI note"
                                      description={topic.ainote}
                                      style={{ marginTop: 8 }}
                                    />
                                  )}
                                </div>
                              ))}
                              {conversationTodos && (
                                <div style={{ padding: 10, background: '#fafafa', border: '1px solid #f0f0f0' }}>
                                  <Text strong>TODO</Text>
                                  <Space direction="vertical" size={4} style={{ display: 'flex', marginTop: 8 }}>
                                    {conversationTodos.map((todo, index) => (
                                      <Text key={`${todo}-${index}`}>{todo}</Text>
                                    ))}
                                  </Space>
                                </div>
                              )}
                            </Space>
                          ) : conversationSummaryText ? (
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{conversationSummaryText}</Text>
                          ) : (
                            <Empty description={conversationEmptyLabel} />
                          )}
                        </Spin>
                      </div>
                      <div style={{ padding: 12, border: '1px solid #f0f0f0', background: '#fafafa' }}>
                        <Text strong>今後追加する詳細</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                            <Tag>音声/話者</Tag>
                            <Tag>写真</Tag>
                            <Tag>飲食</Tag>
                            <Tag>決済</Tag>
                            <Tag>運動量</Tag>
                          </Space>
                        </div>
                      </div>
                    </Space>
                  </div>
                </Col>
                ) : (
                <>
                <Col xs={24} lg={5} style={{ display: 'flex' }}>
                  <div
                    ref={summaryActivityRef}
                    style={{
                      width: '100%',
                      height: summaryMaxHeight ? `${summaryMaxHeight}px` : undefined,
                      minHeight: 0,
                    }}
                  >
                    <Spin spinning={locationAnalysisDerived.loading} style={{ width: '100%' }}>
                      <LocationAnalysisTimelinePanel
                        timelineTitle="アクティビティ"
                        className="diary-summary-activity-panel"
                        items={dayLocationTimelineItems}
                        locations={locationAnalysisDerived.locations}
                        locationNameMap={locationAnalysisDerived.locationNameMap}
                        locationPoiMap={locationAnalysisDerived.locationPoiMap}
                        poiCandidatesByLocationId={locationAnalysisDerived.poiCandidatesByLocationId}
                        searchingPoiLocationId={locationAnalysisDerived.searchingPoiLocationId}
                        linkingPoiLocationId={locationAnalysisDerived.linkingPoiLocationId}
                        selectedTarget={effectiveLocationTimelineTarget}
                        showTimelineEditToggle
                        editButtonLabel="編集"
                        editingButtonLabel="編集終了"
                        editButtonStyle={summaryRegenerateButtonStyle}
                        style={{
                          width: '100%',
                          height: summaryMaxHeight ? `${summaryMaxHeight}px` : '100%',
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        titleRowStyle={summaryPaneTitleRowStyle}
                        scrollStyle={{
                          height: 'auto',
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                        }}
                        onSelect={handleLocationTimelineSelect}
                        onRenameLocation={locationAnalysisDerived.handleRenameLocation}
                        onUpdateLocationSemanticType={locationAnalysisDerived.handleUpdateLocationSemanticType}
                        onOverrideTriplegMode={locationAnalysisDerived.handleOverrideTriplegMode}
                        onDeleteTripleg={locationAnalysisDerived.handleDeleteTripleg}
                        onReassignStaypointLocation={locationAnalysisDerived.handleReassignStaypointLocation}
                        onSearchLocationPoi={locationAnalysisDerived.handleSearchLocationPoi}
                        onLinkLocationPoi={locationAnalysisDerived.handleLinkLocationPoi}
                      />
                    </Spin>
                  </div>
                </Col>
                <Col xs={24} lg={1} style={{ display: 'flex' }}>
                  <div
                    aria-label="アクティビティ時系列"
                    style={{
                      width: '100%',
                      minWidth: 54,
                      height: summaryMaxHeight ? `${summaryMaxHeight}px` : '100%',
                      minHeight: summaryMaxHeight ? undefined : 360,
                      position: 'relative',
                      padding: '20px 0 12px',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 20,
                        bottom: 12,
                        left: 38,
                        width: 2,
                        borderRadius: 999,
                        background: '#d9d9d9',
                      }}
                    >
                      {selectedActivityDayScaleRange && (
                        <div
                          style={{
                            position: 'absolute',
                            top: `${selectedActivityDayScaleRange.startPct}%`,
                            height: `${Math.max(selectedActivityDayScaleRange.endPct - selectedActivityDayScaleRange.startPct, 0.5)}%`,
                            left: -3,
                            width: 8,
                            borderRadius: 999,
                            background: '#1890ff',
                            boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.12)',
                          }}
                        />
                      )}
                      {activityScaleTicks.map(({ hour, label }) => (
                        <div
                          key={hour}
                          style={{
                            position: 'absolute',
                            top: `${(hour / 24) * 100}%`,
                            left: -5,
                            width: 12,
                            height: 1,
                            background: hour % 6 === 0 ? '#8c8c8c' : '#bfbfbf',
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              right: 12,
                              top: -7,
                              width: 34,
                              textAlign: 'right',
                              color: hour % 6 === 0 ? '#595959' : '#8c8c8c',
                              fontSize: 10,
                              lineHeight: '14px',
                              fontVariantNumeric: 'tabular-nums',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col xs={24} lg={6}>
                  <div
                    ref={summaryMiddleRef}
                    style={{
                      height: summaryMaxHeight ? `${summaryMaxHeight}px` : undefined,
                      minHeight: 0,
                      overflowY: summaryMaxHeight ? 'auto' : undefined,
                      paddingRight: summaryMaxHeight ? 4 : undefined,
                    }}
                  >
                    <div style={summaryPaneTitleRowStyle}>
                      <div>
                        <Text strong>イベントサマリー</Text>
                        <Text>: {diaryLabel}</Text>
                        {diarySummary?.updated_at && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            更新: {dayjs(diarySummary.updated_at).format('HH:mm')}
                          </Text>
                        )}
                        {diarySummary?.error_message && (
                          <div style={{ color: '#c41d7f', marginTop: 4 }}>
                            {diarySummary.error_message}
                          </div>
                        )}
                      </div>
                      <Button
                        type="primary"
                        onClick={openDiarySummaryModal}
                        disabled={diarySummarySubmitting || diaryRunning}
                        loading={diarySummarySubmitting}
                        style={summaryRegenerateButtonStyle}
                      >
                        {diaryButtonLabel}
                      </Button>
                    </div>
                    {diaryEvents.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {diaryEvents.map((event, index) => {
                        const isActive = index === activeDiaryIndex;
                        const isSelected = index === selectedTranscriptEventIndex;
                        const hasData = event.hasData !== 0 && event.hasData !== false;
                        const isHighlighted = (playbackHighlightActive ? isActive : isSelected) && hasData;
                        const isCollapsedByActivity = Boolean(selectedActivityTimeRange) &&
                          !eventOverlapsSelectedActivity(index);
                        return (
                          <div
                            key={`${event.start}-${event.end}-${index}`}
                            onClick={() => hasData && handleDiaryEventClick(index)}
                            style={{
                              padding: '10px 12px',
                              borderRadius: 8,
                              border: hasData
                                ? `1px solid ${isHighlighted ? '#1890ff' : '#f0f0f0'}`
                                : '1px dashed #d9d9d9',
                              background: isHighlighted ? '#e6f7ff' : '#fafafa',
                              cursor: hasData ? 'pointer' : 'default',
                              transition: 'background 0.2s, border-color 0.2s',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <Text strong style={{ fontSize: 12, ...(!hasData ? { color: '#999' } : {}) }}>
                                {event.start}-{event.end} {event.title}
                              </Text>
                              {!hasData && (
                                <Tag color="default" style={{ marginInlineEnd: 0, fontSize: 11 }}>
                                  データ無し
                                </Tag>
                              )}
                            </div>
                            {!isCollapsedByActivity && (
                              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                {event.detail}
                              </Text>
                            )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Empty description={diaryEmptyLabel} />
                    )}
                    {geminiUsage && (
                      <div style={{ marginTop: 12 }}>
                        <Text type="secondary">メタ情報</Text>
                        <div style={{ marginTop: 4, fontSize: 12, color: '#666', display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span>
                            入力 合計:
                            {promptTotalTokens !== undefined ? ` ${promptTotalTokens}` : ' -'} tokens
                            {textTokens !== undefined || videoTokens !== undefined ? (
                              <>
                                （TEXT: {textTokens ?? '-'}, VIDEO: {videoTokens ?? '-'}）
                              </>
                            ) : null}
                          </span>
                          <span>
                            出力 合計:
                            {outputTotalTokens !== undefined ? ` ${outputTotalTokens}` : ' -'} tokens
                            {thoughtsTokens !== undefined || outputTokens !== undefined ? (
                              <>
                                （思考: {thoughtsTokens ?? '-'}, 出力: {outputTokens ?? '-'}）
                              </>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    )}
                    <Modal
                      title="サマリー再生成"
                      open={diarySummaryModalOpen}
                      onCancel={() => !diarySummarySubmitting && setDiarySummaryModalOpen(false)}
                      onOk={() => requestDiarySummary(diarySummaryRequestOptions)}
                      okText={diaryButtonLabel}
                      cancelText="キャンセル"
                      confirmLoading={diarySummarySubmitting}
                      okButtonProps={{ disabled: diaryRunning }}
                      destroyOnClose={false}
                    >
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <div>
                          <Text strong>Max output tokens</Text>
                          <div style={{ marginTop: 6 }}>
                            <InputNumber
                              min={1}
                              max={65535}
                              style={{ width: '100%' }}
                              value={diarySummaryRequestOptions.maxOutputTokens}
                              onChange={(value) =>
                                setDiarySummaryRequestOptions((prev) => ({
                                  ...prev,
                                  maxOutputTokens: typeof value === 'number' ? value : prev.maxOutputTokens,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                          <div style={{ flex: 1 }}>
                            <Text strong>includeThoughts</Text>
                            <div style={{ marginTop: 6 }}>
                              <Switch
                                checked={diarySummaryRequestOptions.includeThoughts}
                                onChange={(checked) =>
                                  setDiarySummaryRequestOptions((prev) => ({
                                    ...prev,
                                    includeThoughts: checked,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <Text strong>compaction</Text>
                            <div style={{ marginTop: 6 }}>
                              <Switch
                                checked={diarySummaryRequestOptions.enableCompaction}
                                onChange={(checked) =>
                                  setDiarySummaryRequestOptions((prev) => ({
                                    ...prev,
                                    enableCompaction: checked,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Text strong>thinking budget</Text>
                          <div style={{ marginTop: 6 }}>
                            <InputNumber
                              min={0}
                              max={65535}
                              placeholder="未指定"
                              style={{ width: '100%' }}
                              value={diarySummaryRequestOptions.thinkingBudget ?? undefined}
                              onChange={(value) =>
                                setDiarySummaryRequestOptions((prev) => ({
                                  ...prev,
                                  thinkingBudget: typeof value === 'number' ? value : null,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <Text strong>追加プロンプト</Text>
                          <div style={{ marginTop: 6 }}>
                            <Input.TextArea
                              rows={6}
                              maxLength={2000}
                              showCount
                              placeholder="今回の要約で特に見たい観点や指示を入力"
                              value={diarySummaryRequestOptions.userPrompt}
                              onChange={(event) =>
                                setDiarySummaryRequestOptions((prev) => ({
                                  ...prev,
                                  userPrompt: event.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </Space>
                    </Modal>
                  </div>
                </Col>
                <Col xs={24} lg={6} style={{ display: 'flex' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      height: summaryMaxHeight ? `${summaryMaxHeight}px` : 'auto',
                      minHeight: 0,
                      overflowY: summaryMaxHeight ? 'auto' : undefined,
                    }}
                  >
                    <Tabs
                      defaultActiveKey="people"
                      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                      tabBarStyle={{ marginBottom: 12 }}
                      items={[
                        {
                          key: 'people',
                          label: '人物',
                          children: (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                height: '100%',
                              }}
                            >
                              <div style={{ marginBottom: 8 }}>
                                <Text>人物:</Text>{' '}
                                <Text strong>{dailyEncounterPeople.length}人</Text>
                              </div>
                              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
                                {renderPeopleCards(dailyEncounterPeople, 'この日の人物はまだありません')}
                              </div>
                            </div>
                          ),
                        },
                        {
                          key: 'food',
                          label: '飲食',
                          children: (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                                <div>
                                  <Text>飲食:</Text>{' '}
                                  <Text strong>{foodLabel}</Text>
                                  {foodSummary?.updated_at && (
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                      更新: {dayjs(foodSummary.updated_at).format('HH:mm')}
                                    </Text>
                                  )}
                                  {foodSummary?.error_message && (
                                    <div style={{ color: '#c41d7f', marginTop: 4 }}>
                                      {foodSummary.error_message}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  type="primary"
                                  onClick={requestFoodSummary}
                                  disabled={foodRunning}
                                  loading={foodSummarySubmitting}
                                >
                                  {foodButtonLabel}
                                </Button>
                              </div>
                              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
                                {foodSummaryLoading || foodSummarySubmitting ? (
                                  <div style={{ padding: '12px 0' }}>
                                    <Spin />
                                  </div>
                                ) : foodItems.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {foodItems.map((item, index) => (
                                      <div
                                        key={`${item.start ?? 'food'}-${index}`}
                                        style={{
                                          padding: '8px 10px',
                                          borderRadius: 8,
                                          border: '1px solid #f0f0f0',
                                          background: '#fff',
                                        }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                          <Text strong>{item.start ?? '--:--'}</Text>
                                          {item.calory && (
                                            <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                                              {item.calory}
                                            </Tag>
                                          )}
                                        </div>
                                        {item.place && (
                                          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                                            {item.place}
                                          </Text>
                                        )}
                                        {item.contents && (
                                          <Text style={{ whiteSpace: 'pre-wrap' }}>{item.contents}</Text>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : foodSummaryText ? (
                                  <div
                                    style={{
                                      padding: '8px 10px',
                                      borderRadius: 8,
                                      border: '1px solid #f0f0f0',
                                      background: '#fff',
                                    }}
                                  >
                                    <Text style={{ whiteSpace: 'pre-wrap' }}>{foodSummaryText}</Text>
                                  </div>
                                ) : (
                                  <Empty description={foodEmptyLabel} />
                                )}
                              </div>
                              {foodUsage && (
                                <div style={{ marginTop: 10 }}>
                                  <Text type="secondary">メタ情報</Text>
                                  <div style={{ marginTop: 4, fontSize: 12, color: '#666', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <span>
                                      入力 合計:
                                      {foodPromptTokens !== undefined ? ` ${foodPromptTokens}` : ' -'} tokens
                                      {foodTextTokens !== undefined || foodVideoTokens !== undefined ? (
                                        <>
                                          （TEXT: {foodTextTokens ?? '-'}, VIDEO: {foodVideoTokens ?? '-'}）
                                        </>
                                      ) : null}
                                    </span>
                                    <span>
                                      出力 合計:
                                      {foodOutputTotalTokens !== undefined ? ` ${foodOutputTotalTokens}` : ' -'} tokens
                                      {foodThoughtsTokens !== undefined || foodOutputTokens !== undefined ? (
                                        <>
                                          （思考: {foodThoughtsTokens ?? '-'}, 出力: {foodOutputTokens ?? '-'}）
                                        </>
                                      ) : null}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ),
                        },
                      ]}
                    />
                  </div>
                </Col>
                </>
                )}
              </Row>
            </div>
          </Col>

          <Col span={24}>
            <div style={diarySectionStyle}>
              <div style={diarySectionTitleStyle}>位置情報</div>
              <Spin spinning={locationAnalysisDerived.loading}>
                <LocationAnalysisResultView
                  framed={false}
                  items={locationAnalysisDerived.timelineItems}
                  staypoints={locationAnalysisDerived.staypoints}
                  triplegs={locationAnalysisDerived.triplegs}
                  trips={locationAnalysisDerived.trips}
                  locations={locationAnalysisDerived.locations}
                  visibleLocations={locationAnalysisDerived.visibleLocations}
                  locationExtentBufferM={locationAnalysisDerived.locationExtentBufferM}
                  locationNameMap={locationAnalysisDerived.locationNameMap}
                  locationPoiMap={locationAnalysisDerived.locationPoiMap}
                  poiCandidatesByLocationId={locationAnalysisDerived.poiCandidatesByLocationId}
                  searchingPoiLocationId={locationAnalysisDerived.searchingPoiLocationId}
                  linkingPoiLocationId={locationAnalysisDerived.linkingPoiLocationId}
                  selectedTarget={locationAnalysisDerived.selectedDerivedTarget}
                  onSelect={(target) => locationAnalysisDerived.setSelectedDerivedTarget(target)}
                  onRenameLocation={locationAnalysisDerived.handleRenameLocation}
                  onUpdateLocationSemanticType={locationAnalysisDerived.handleUpdateLocationSemanticType}
                  onOverrideTriplegMode={locationAnalysisDerived.handleOverrideTriplegMode}
                  onDeleteTripleg={locationAnalysisDerived.handleDeleteTripleg}
                  onReassignStaypointLocation={locationAnalysisDerived.handleReassignStaypointLocation}
                  onSearchLocationPoi={locationAnalysisDerived.handleSearchLocationPoi}
                  onLinkLocationPoi={locationAnalysisDerived.handleLinkLocationPoi}
                />
              </Spin>
            </div>
          </Col>

          <Col span={24}>
            <div style={diarySectionStyle}>
              <div style={diarySectionTitleStyle}>音声</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text>件数:</Text>{' '}
                  <Text strong>{audioCount}</Text>
                </div>
                {rangeQuery && (
                  <Link to={`/devices/${selectedDevice.id}/audio?${rangeQuery}`}>
                    音声ページへ
                  </Link>
                )}
              </div>
            </div>
          </Col>

          <Col span={24}>
            <div style={diarySectionStyle}>
              <div style={diarySectionTitleStyle}>写真</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text>件数:</Text>{' '}
                  <Text strong>{photoCount}</Text>
                </div>
                {rangeQuery && (
                  <Link to={`/devices/${selectedDevice.id}/photos?${rangeQuery}`}>
                    写真ページへ
                  </Link>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default DiaryPage;
